import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiCpu, FiCalendar, FiRefreshCw, FiCheck, FiSearch, FiPlus, FiX, FiTrash2, FiZap, FiTrendingUp, FiBookOpen, FiAlertTriangle, FiClock, FiChevronDown, FiChevronUp, FiMessageSquare, FiExternalLink, FiEdit2, FiStar, FiDownload, FiMaximize2, FiEye, FiEyeOff, FiList, FiImage, FiLock, FiUnlock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../../hooks/AuthContext';
import useCalendarNotifications from '../hooks/useCalendarNotifications';
import {
    getSchedules,
    getSchedulesByInstructor,
    getCoursesByIds,
    updateSchedule,
    createSchedule,
    saveGeneratedSchedule,
    getInstructors,
    getCourses,
    deleteSchedule,
    getCalendarConfig,
    getAsesores,
    getDraftsRealtime,
    autoSaveDraft,
    loadDraftForCourse,
    deleteDraft,
    deleteDraftById,
    setCalendarWorkspace,
    updateCourse,
    getCalendarWorkspaceId,
} from '../services';
import { generateSchedule, analyzeCalendarWithMemory } from '../../../services/gemini';
import { simplifyGoogleEventsForAI } from '../../../services/googleCalendar';
import { useGoogleCalendar } from '../../../contexts/GoogleCalendarContext';
import { subscribeToZoomAccounts, updateZoomAccount } from '../../../services/zoomAccountsService';
import { createMissingZoomRooms, getZoomRoomSettings, saveZoomRoomSettings, zoomErrorMessage } from '../../../services/zoomApiService';
import {
    distributeDay, dayEventsOf, missingRoomItemsFrom, peakSimultaneous, concurrentDuring, fmtTimeLabel, getEventType,
    getEventZoomAccountId, MAX_SIMULTANEOUS_PER_ACCOUNT, MASTERCLASS_DURATION_MIN, CLASE_BUFFER_MIN, CLASE_FALLBACK_MIN,
    DEFAULT_DIST_OPTIONS,
} from '../utils/zoomDistribution';
import ZoomPeriodPanel from '../components/ZoomPeriodPanel';
import '../components/zoomDist.css';
import DragDropCalendar from '../components/DragDropCalendar';
import { buildEnumCache } from '../utils/eventEnumeration';
import EventPopover from '../components/EventPopover';
import GoogleCalendarConnect from '../components/GoogleCalendarConnect';
import AISchedulingChat from '../components/AISchedulingChat';
import GCalImportModal from '../components/GCalImportModal';
import GoogleCalendarExportPanel from '../components/GoogleCalendarExportPanel';
import InstructorSearchSelect from '../components/InstructorSearchSelect';
import HxSelect from '../../../components/ui/HxSelect';
import { BookOpen as LBook, Users as LUsers, UserRound as LUser } from 'lucide-react';
import InstructorAvailabilityCard from '../components/InstructorAvailabilityCard';
import AgendaExpandModal from '../components/AgendaExpandModal';
import exportAgendaToXlsx from '../utils/exportAgendaXlsx';
import exportAgendaToImage from '../utils/exportAgendaImage';

const MEMORY_KEY = 'cal_analysis_memory';

const loadMemory = () => {
    try {
        const raw = localStorage.getItem(MEMORY_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const saveMemory = (analysis) => {
    try {
        const toSave = {
            fechaAnalisis: new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }),
            resumen: analysis.resumen || '',
            cursos: (analysis.cursos || []).map(c => ({
                nombre: c.nombre,
                masterclassActual: c.masterclassActual,
                sesionActual: c.sesionActual,
                estado: c.estado,
                fechaUltimoEvento: c.fechaUltimoEvento,
            })),
        };
        localStorage.setItem(MEMORY_KEY, JSON.stringify(toSave));
    } catch { /* ignorar error de storage */ }
};

// Local date string helper (avoids UTC timezone shift)
const toLocalDateStr = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};

const CalendarPage = ({ instructorMode = false, viewerInstructorId = null, onInstructorEventClick = null } = {}) => {
    const { user } = useAuth();
    useCalendarNotifications(); // Calendar event reminders
    const [events, setEvents] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [courses, setCourses] = useState([]);
    const [asesores, setAsesores] = useState([]);
    const [loading, setLoading] = useState(true);

    // Google Calendar
    const {
        isConnected: gcalConnected,
        googleEvents,
        syncEvents: syncGoogleEvents,
    } = useGoogleCalendar();

    const [visibleRange, setVisibleRange] = useState(() => {
        const today = new Date();
        return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: new Date(today.getFullYear(), today.getMonth() + 1, 6) };
    });

    // Search/Filter State
    const [searchTerm, setSearchTerm] = useState('');

    // AI Wizard State
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [targetMonth, setTargetMonth] = useState(new Date().getMonth() + 1);
    const [targetYear, setTargetYear] = useState(new Date().getFullYear());
    const [selectedCourseId, setSelectedCourseId] = useState(''); // Empty = all courses
    const [masterclassCount, setMasterclassCount] = useState(6); // Default MC count
    const [regenerateMode, setRegenerateMode] = useState(false); // Delete existing before generate

    // Cancel Event Modal State
    const [cancelModal, setCancelModal] = useState(null); // { eventId, event }
    const [cancelAssignee, setCancelAssignee] = useState('');
    const [cancelDueDate, setCancelDueDate] = useState('');
    const [allUsers, setAllUsers] = useState([]);

    // ── Opciones para los desplegables con buscador (HxSelect) ──
    const instColorOf = (id) => instructors.find(i => i.id === id)?.color || '#94a3b8';
    const courseOpts = (list, emptyLabel) => [
        ...(emptyLabel ? [{ value: '', label: emptyLabel, icon: LBook }] : []),
        ...[...list].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => ({
            value: c.id, label: c.name, sub: c.abreviatura || undefined, color: instColorOf(c.instructorId),
        })),
    ];
    const instructorOpts = (emptyLabel) => [
        ...(emptyLabel ? [{ value: '', label: emptyLabel, icon: LUsers }] : []),
        ...[...instructors].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(i => ({
            value: i.id, label: i.name, sub: i.email || undefined, color: i.color || '#94a3b8', photo: i.photoURL || undefined,
        })),
    ];
    const userOpts = (list, emptyLabel) => [
        ...(emptyLabel ? [{ value: '', label: emptyLabel, icon: LUser }] : []),
        ...[...list].sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => ({
            value: u.email, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#0a5cff',
        })),
    ];


    // Manual Event Creation State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newEvent, setNewEvent] = useState({
        type: 'masterclass',
        title: '',
        instructorId: '',
        courseId: '',
        start: '',
        end: '',
        notes: ''
    });
    const [isCreating, setIsCreating] = useState(false);

    // Persist calendar preferences per user
    const calPrefsKey = user?.uid ? `cal_prefs_${user.uid}` : null;
    const savedCalPrefs = useMemo(() => {
        if (!calPrefsKey) return null;
        try { return JSON.parse(localStorage.getItem(calPrefsKey)); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calPrefsKey]);

    // Schedule Grid Panel State
    const [showGrid, setShowGrid] = useState(savedCalPrefs?.showGrid || false);
    const [calLabel, setCalLabel] = useState('');

    const [calView, setCalView] = useState(savedCalPrefs?.calView || 'month');
    const [calViewLabel, setCalViewLabel] = useState(savedCalPrefs?.calViewLabel || 'Mes');
    const [showViewMenu, setShowViewMenu] = useState(false);
    const calNavRef = useRef(null);
    const calViewRef = useRef(null);
    const viewMenuRef = useRef(null);
    useEffect(() => {
        const close = (e) => { if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setShowViewMenu(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);
    const [gridWidth, setGridWidth] = useState(() => {
        const saved = localStorage.getItem('calGridWidth');
        return saved ? parseInt(saved) : 440;
    });
    const [isResizingGrid, setIsResizingGrid] = useState(false);
    const gridResizeRef = useCallback((node) => {
        if (!node) return;
        const handleMouseDown = (e) => {
            e.preventDefault();
            setIsResizingGrid(true);
            const startX = e.clientX;
            const startW = gridWidth;
            const onMove = (ev) => {
                const newW = Math.max(320, Math.min(700, startW + (startX - ev.clientX)));
                setGridWidth(newW);
            };
            const onUp = () => {
                setIsResizingGrid(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                localStorage.setItem('calGridWidth', String(gridWidth));
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        };
        node.onmousedown = handleMouseDown;
    }, [gridWidth]);

    // Save width on change
    useEffect(() => {
        if (gridWidth !== 440) localStorage.setItem('calGridWidth', String(gridWidth));
    }, [gridWidth]);

    // ── Panel de evento en modo columna (ancho redimensionable) ──────────────
    const [eventPanelWidth, setEventPanelWidth] = useState(() => {
        const saved = localStorage.getItem('calEventPanelWidth');
        return saved ? parseInt(saved) : 420;
    });
    const [isResizingEventPanel, setIsResizingEventPanel] = useState(false);
    const eventPanelResizeRef = useCallback((node) => {
        if (!node) return;
        const handleMouseDown = (e) => {
            e.preventDefault();
            setIsResizingEventPanel(true);
            const startX = e.clientX;
            const startW = eventPanelWidth;
            const onMove = (ev) => {
                const newW = Math.max(340, Math.min(640, startW + (startX - ev.clientX)));
                setEventPanelWidth(newW);
            };
            const onUp = () => {
                setIsResizingEventPanel(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                localStorage.setItem('calEventPanelWidth', String(eventPanelWidth));
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        };
        node.onmousedown = handleMouseDown;
    }, [eventPanelWidth]);
    useEffect(() => {
        localStorage.setItem('calEventPanelWidth', String(eventPanelWidth));
    }, [eventPanelWidth]);

    const [gridCourseId, setGridCourseId] = useState('');
    const [gridInstructorId, setGridInstructorId] = useState('');
    const [agendaHighlightCourseId, setAgendaHighlightCourseId] = useState(null);
    const [showAgendaExpand, setShowAgendaExpand] = useState(false);
    const [showHiddenGroups, setShowHiddenGroups] = useState(false);
    const [agendaOpenCourseId, setAgendaOpenCourseId] = useState(null);
    const [reprogramTarget, setReprogramTarget] = useState(null);
    const [reprogramForm, setReprogramForm] = useState({ date: '', startTime: '', endTime: '' });
    const [showCancelledList, setShowCancelledList] = useState(false);
    const [showCourseCancelledList, setShowCourseCancelledList] = useState(false);
    const [showExcelChooser, setShowExcelChooser] = useState(false);
    const groupRefs = useRef({});

    // ── Grupos desactivados (a nivel curso) ──
    const isGroupInactive = (courseId, groupNumber) => {
        const course = courses.find(c => c.id === courseId);
        return Array.isArray(course?.inactiveGroups) && course.inactiveGroups.includes(Number(groupNumber));
    };

    const toggleGroupInactive = async (courseId, groupNumber) => {
        const course = courses.find(c => c.id === courseId);
        if (!course) return;
        const current = Array.isArray(course.inactiveGroups) ? course.inactiveGroups : [];
        const gn = Number(groupNumber);
        const next = current.includes(gn) ? current.filter(x => x !== gn) : [...current, gn];
        // Optimista: colapsa/expande al instante sin esperar el round-trip a Firestore
        setCourses(prev => prev.map(c => c.id === courseId ? { ...c, inactiveGroups: next } : c));
        try {
            await updateCourse(courseId, { inactiveGroups: next });
        } catch (err) {
            console.error('Error al actualizar inactiveGroups:', err);
            toast.error('Error al actualizar grupo');
            setCourses(prev => prev.map(c => c.id === courseId ? { ...c, inactiveGroups: current } : c));
        }
    };

    // ── Reprogramar una clase cancelada (nueva fecha/hora + reactivar) ──
    const openReprogram = (ev) => {
        const s = ev.start instanceof Date ? ev.start : new Date(ev.start);
        const e = ev.end instanceof Date ? ev.end : new Date(ev.end);
        setReprogramForm({
            date: toLocalDateStr(s),
            startTime: `${String(s.getHours()).padStart(2, '0')}:${String(s.getMinutes()).padStart(2, '0')}`,
            endTime: `${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`,
        });
        setReprogramTarget(ev);
    };

    const executeReprogram = async () => {
        if (!reprogramTarget) return;
        const { date, startTime, endTime } = reprogramForm;
        if (!date || !startTime || !endTime) { toast.error('Completa fecha y horas'); return; }
        const [y, m, d] = date.split('-').map(Number);
        const [sh, sm] = startTime.split(':').map(Number);
        const [eh, em] = endTime.split(':').map(Number);
        const newStart = new Date(y, m - 1, d, sh, sm, 0);
        const newEnd = new Date(y, m - 1, d, eh, em, 0);
        try {
            await updateSchedule(reprogramTarget.id, { start: newStart, end: newEnd, status: 'confirmed', cancelledAt: null, reprogramTaskId: null });
            setEvents(prev => prev.map(x => x.id === reprogramTarget.id ? { ...x, start: newStart, end: newEnd, status: 'confirmed' } : x));
            toast.success('Clase reprogramada');
            setReprogramTarget(null);
        } catch { toast.error('Error al reprogramar'); }
    };

    // ── Navegación entre Modo Agenda y Modo Programador ──
    const goToAgenda = () => {
        setShowInstructorTable(true);
        if (gridInstructorId) setFilterInstructorId(gridInstructorId);
        else if (!filterInstructorId && instructors.length > 0) setFilterInstructorId(instructors[0].id);
        setAgendaHighlightCourseId(null);
        setAgendaOpenCourseId(null);
    };
    const goToProgramador = () => {
        if (filterInstructorId && filterInstructorId !== gridInstructorId) {
            setGridInstructorId(filterInstructorId);
            setGridCourseId('');
        }
        setAgendaHighlightCourseId(null);
        setAgendaOpenCourseId(null);
        setShowInstructorTable(false);
    };
    // Cerrar el panel: quita filtros y deja el calendario normal
    const closePanel = () => {
        setShowGrid(false);
        setShowInstructorTable(false);
        setFilterInstructorId('');
        setAgendaHighlightCourseId(null);
        setAgendaOpenCourseId(null);
    };
    const [gridRows, setGridRows] = useState([{ id: Date.now(), type: 'masterclass', date: '', startTime: '18:00', endTime: '20:00', alert: '', override: false }]);
    const [isInsertingGrid, setIsInsertingGrid] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [filterInstructorId, setFilterInstructorId] = useState(savedCalPrefs?.filterInstructorId || '');
    const [showInstructorTable, setShowInstructorTable] = useState(savedCalPrefs?.showInstructorTable || false);
    const [agendaViewMode, setAgendaViewMode] = useState(savedCalPrefs?.agendaViewMode || 'fecha');
    // Programming mode: null = show selector, 'edit' = edit existing, 'add' = add to group, 'new' = new group
    const [gridMode, setGridMode] = useState(null);
    const [existingGroupNum, setExistingGroupNum] = useState(1);

    // Modo instructor: el panel siempre es Agenda (nunca Programador) y filtra a su instructor.
    useEffect(() => {
        if (instructorMode && showGrid) {
            setShowInstructorTable(true);
            if (viewerInstructorId) setFilterInstructorId(viewerInstructorId);
        }
    }, [instructorMode, showGrid, viewerInstructorId]);

    // Persist calendar navigation preferences per user
    useEffect(() => {
        if (!calPrefsKey) return;
        const prefs = {
            calView, calViewLabel, showGrid,
            filterInstructorId, showInstructorTable, agendaViewMode,
        };
        localStorage.setItem(calPrefsKey, JSON.stringify(prefs));
    }, [calPrefsKey, calView, calViewLabel, showGrid, filterInstructorId, showInstructorTable, agendaViewMode]);

    const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved'
    const [draftHistory, setDraftHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const autoSaveTimer = useRef(null);
    const dirtyRef = useRef(false); // only autosave when user actually edited rows

    // Auto-save to Firestore every 3s of inactivity
    useEffect(() => {
        const hasContent = gridRows.some(r => r.date);
        setHasDraft(hasContent);
        if (!hasContent || !user?.uid) return;
        if (!dirtyRef.current) return; // skip when rows came from load, not user edits

        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(async () => {
            setAutoSaveStatus('saving');
            try {
                await autoSaveDraft(gridCourseId, gridInstructorId, gridRows, user, courses, instructors);
                dirtyRef.current = false;
                setAutoSaveStatus('saved');
                setTimeout(() => setAutoSaveStatus(''), 2000);
            } catch (e) { console.error('Auto-save error:', e); setAutoSaveStatus(''); }
        }, 3000);

        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    }, [gridRows, gridCourseId, gridInstructorId]);

    // Load draft automatically when course+instructor changes
    useEffect(() => {
        if (!gridCourseId && !gridInstructorId) return;
        (async () => {
            try {
                const draft = await loadDraftForCourse(gridCourseId, gridInstructorId);
                if (draft && draft.rows?.length > 0) {
                    dirtyRef.current = false;
                    setGridRows(draft.rows);
                    setDraftHistory(draft.history || []);
                    setHasDraft(true);
                }
            } catch { }
        })();
    }, [gridCourseId, gridInstructorId]);

    // Subscribe to all drafts for the list
    useEffect(() => {
        const unsub = getDraftsRealtime((drafts) => setSavedDrafts(drafts));
        return () => unsub();
    }, []);

    const clearDraft = async () => {
        dirtyRef.current = false;
        setGridRows([{ id: Date.now(), type: 'masterclass', date: '', startTime: '18:00', endTime: '20:00', alert: '', override: false }]);
        setHasDraft(false);
        setDraftHistory([]);
        if (gridCourseId || gridInstructorId) {
            try { await deleteDraft(gridCourseId, gridInstructorId); } catch { }
        }
    };

    const restoreHistoryVersion = (version) => {
        if (!version.rows) return;
        dirtyRef.current = true;
        setGridRows(version.rows);
        setShowHistory(false);
        toast.success('Versión restaurada');
    };

    const loadDraftFromList = (draft) => {
        dirtyRef.current = false;
        setGridCourseId(draft.courseId || '');
        setGridInstructorId(draft.instructorId || '');
        setGridRows(draft.rows?.length > 0 ? draft.rows : [{ id: Date.now(), type: 'masterclass', date: '', startTime: '18:00', endTime: '20:00', alert: '', override: false }]);
        setDraftHistory(draft.history || []);
        setShowDraftsList(false);
    };

    const deleteDraftFromList = async (draft) => {
        try {
            await deleteDraftById(draft.id);
            toast.success('Borrador eliminado');
        } catch { toast.error('Error al eliminar'); }
    };

    // Conflict summary computation
    // Helper: count asesores available on a given weekday and hour
    const getAvailableAsesoresForSlot = useCallback((dateStr, timeStr) => {
        if (asesores.length === 0) return 2; // fallback
        const dayOfWeek = new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        return asesores.filter(a => {
            const cfg = a.asesorConfig || {};
            const days = cfg.availableDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
            if (!days.includes(dayOfWeek)) return false;
            const slots = cfg.availableSlots || ['18:00', '19:00'];
            if (slots.length > 0 && !slots.includes(timeStr)) return false;
            return true;
        }).length || 1;
    }, [asesores]);

    const gridConflictSummary = useMemo(() => {
        const slotMap = {};
        // Count ONLY existing calendar MCs (not grid draft rows — those are not saved yet)
        events.forEach(e => {
            if (e.resource?.type !== 'masterclass' && e.type !== 'masterclass') return;
            const eDate = toLocalDateStr(e.start);
            const eTime = String(new Date(e.start).getHours()).padStart(2, '0') + ':' + String(new Date(e.start).getMinutes()).padStart(2, '0');
            const key = `${eDate}_${eTime}`;
            slotMap[key] = (slotMap[key] || 0) + 1;
        });
        // Filter slots where count > available asesores for that day+hour
        return Object.entries(slotMap)
            .map(([key, count]) => {
                const [date, time] = key.split('_');
                const available = getAvailableAsesoresForSlot(date, time);
                if (count <= available) return null;
                const d = new Date(date + 'T12:00:00');
                const dateLabel = d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
                const [hh, mm] = time.split(':');
                const h = parseInt(hh, 10);
                const ampm = h >= 12 ? 'pm' : 'am';
                const h12 = h % 12 || 12;
                const timeLabel = `${h12}:${mm} ${ampm}`;
                return { date, time, timeLabel, dateLabel, count, available };
            })
            .filter(Boolean)
            .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
    }, [events, asesores, getAvailableAsesoresForSlot]);

    // Global calendar alerts: daily analysis with conflicts, capacity, suggestions
    const DAILY_MC_GOAL = 6;
    const ALL_SLOTS = ['17:00','18:00','19:00','20:00','21:00','22:00'];
    const [showAlerts, setShowAlerts] = useState(false);

    const calendarAlertsDays = useMemo(() => {
        if (asesores.length === 0) return [];
        const now = new Date();
        // Group future MCs by date
        const byDate = {};
        events.forEach(e => {
            if (e.resource?.type !== 'masterclass' && e.type !== 'masterclass') return;
            const eStart = e.start instanceof Date ? e.start : new Date(e.start);
            if (eStart < now) return;
            const eDate = toLocalDateStr(e.start);
            const eTime = String(eStart.getHours()).padStart(2, '0') + ':' + String(eStart.getMinutes()).padStart(2, '0');
            if (!byDate[eDate]) byDate[eDate] = { date: eDate, slots: {}, totalMC: 0 };
            byDate[eDate].totalMC++;
            if (!byDate[eDate].slots[eTime]) byDate[eDate].slots[eTime] = 0;
            byDate[eDate].slots[eTime]++;
        });
        return Object.values(byDate)
            .map(day => {
                const d = new Date(day.date + 'T12:00:00');
                const dateLabel = d.toLocaleDateString('es', { weekday: 'long', day: '2-digit', month: 'short' });
                const dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                // Analyze each slot
                const slotDetails = Object.entries(day.slots).map(([time, count]) => {
                    const avail = getAvailableAsesoresForSlot(day.date, time);
                    const [hh, mm] = time.split(':');
                    const h = parseInt(hh, 10);
                    const timeLabel = `${h % 12 || 12}:${mm} ${h >= 12 ? 'pm' : 'am'}`;
                    return { time, timeLabel, count, available: avail, excess: count - avail, free: Math.max(0, avail - count) };
                }).sort((a, b) => a.time.localeCompare(b.time));
                const hasConflict = slotDetails.some(s => s.excess > 0);
                // Free slots where asesores are available but no MC scheduled
                const freeSlots = ALL_SLOTS
                    .filter(t => !day.slots[t])
                    .map(t => {
                        const avail = getAvailableAsesoresForSlot(day.date, t);
                        if (avail === 0) return null;
                        const [hh, mm] = t.split(':');
                        const h = parseInt(hh, 10);
                        return { time: t, timeLabel: `${h % 12 || 12}:${mm} ${h >= 12 ? 'pm' : 'am'}`, available: avail };
                    })
                    .filter(Boolean);
                const remaining = DAILY_MC_GOAL - day.totalMC;
                return { ...day, dateLabel, dayOfWeek, slotDetails, hasConflict, freeSlots, remaining };
            })
            .filter(day => day.hasConflict || day.totalMC < DAILY_MC_GOAL)
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [events, asesores, getAvailableAsesoresForSlot]);
    const calendarAlerts = calendarAlertsDays.filter(d => d.hasConflict);

    // ── Zoom distribution ──
    const [zoomAccounts, setZoomAccounts] = useState([]);
    const [showZoomDist, setShowZoomDist] = useState(false);
    const [zoomDistDate, setZoomDistDate] = useState(() => toLocalDateStr(new Date()));
    const [zoomDistResult, setZoomDistResult] = useState(null);
    const [zoomDistSaving, setZoomDistSaving] = useState(false);
    const [priorityModal, setPriorityModal] = useState(null); // { account, selectedIds: Set }
    const [zoomDistWarnings, setZoomDistWarnings] = useState([]);
    const [creatingRooms, setCreatingRooms] = useState(false);
    const [autoCreateOnDistribute, setAutoCreateOnDistribute] = useState(true);
    const [redistributePending, setRedistributePending] = useState(false);
    const accountsAtCreateRef = useRef(null);
    const [zoomDistIsExisting, setZoomDistIsExisting] = useState(false); // result loaded from saved calendar data
    const [zoomRecalcConfirm, setZoomRecalcConfirm] = useState(false);   // confirm dialog before overwriting
    const [lockedEvents, setLockedEvents] = useState(new Set());          // eventIds no recalculados
    const [manualNoZoomEvents, setManualNoZoomEvents] = useState(new Set()); // eventIds marcados sin Zoom
    const [zoomDistTab, setZoomDistTab] = useState('day'); // 'day' | 'period'
    const [roomSettings, setRoomSettings] = useState(null);
    const distOptions = useMemo(() => ({
        reuseMcRooms: roomSettings?.reuseMcRooms ?? DEFAULT_DIST_OPTIONS.reuseMcRooms,
        reuseMcHour: roomSettings?.reuseMcHour ?? DEFAULT_DIST_OPTIONS.reuseMcHour,
    }), [roomSettings]);

    useEffect(() => {
        const unsub = subscribeToZoomAccounts(accs => setZoomAccounts(accs));
        return () => unsub();
    }, []);

    useEffect(() => {
        const ws = getCalendarWorkspaceId();
        if (!ws) return;
        getZoomRoomSettings(ws).then(st => { setRoomSettings(st); setAutoCreateOnDistribute(st.autoCreateOnDistribute !== false); }).catch(() => {});
    }, [zoomAccounts.length]);

    // Reglas, duraciones y reparto viven en utils/zoomDistribution.js (con tests): el mismo motor
    // distribuye un día y analiza un periodo.
    const distributeZoom = useCallback((dateStr, lockedEventIds = new Set(), currentResult = null) => {
        const { assignments, warnings } = distributeDay({
            dayEvents: dayEventsOf(events, dateStr), accounts: zoomAccounts, courses,
            lockedEventIds, currentResult, options: distOptions,
        });
        setZoomDistWarnings(warnings);
        return assignments.length ? assignments : null;
    }, [events, zoomAccounts, courses, distOptions]);

    // Reconstruye el resultado de distribución a partir de lo YA guardado en el
    // calendario. Devuelve null si el día no tiene eventos o ninguno tiene cuenta Zoom.
    const buildExistingZoomResult = useCallback((dateStr) => {
        const dayEvents = events
            .filter(e => toLocalDateStr(e.start) === dateStr && e.status !== 'cancelled')
            .sort((a, b) => new Date(a.start) - new Date(b.start));
        if (dayEvents.length === 0) return null;
        if (!dayEvents.some(e => getEventZoomAccountId(e))) return null;

        const ef = (e, k) => (e?.[k] != null ? e[k] : e?.resource?.[k]);
        return dayEvents.map(e => {
            const s = e.start instanceof Date ? e.start : new Date(e.start);
            const end = e.end instanceof Date ? e.end : new Date(e.end);
            const startMin = s.getHours() * 60 + s.getMinutes();
            const type = getEventType(e);
            let endMin = end.getHours() * 60 + end.getMinutes();
            if (endMin <= startMin) endMin = startMin + (type === 'masterclass' ? MASTERCLASS_DURATION_MIN : CLASE_FALLBACK_MIN);
            const effectiveEndMin = type === 'masterclass'
                ? startMin + MASTERCLASS_DURATION_MIN
                : endMin + CLASE_BUFFER_MIN;
            const accountId = getEventZoomAccountId(e);
            const account = zoomAccounts.find(z => z.id === accountId);
            const zoomLink = type === 'masterclass' ? ef(e, 'mcLiveUrl') : ef(e, 'claseLiveUrl');
            const zoomMeetingId = type === 'masterclass' ? ef(e, 'mcMeetingId') : ef(e, 'claseMeetingId');
            const zoomPassword = type === 'masterclass' ? ef(e, 'mcPassword') : ef(e, 'clasePassword');
            const pool = type === 'masterclass' ? (account?.meetings || []) : (account?.claseMeetings || []);
            const matches = m => (zoomMeetingId && m.meetingId === zoomMeetingId) || (zoomLink && m.zoomLink === zoomLink);
            let mtg = pool.find(matches);
            // Clase guardada en la sala de una MC (regla de las 5 pm): no es su sala de clase.
            const reusedMcRoom = !mtg && type !== 'masterclass' && !!(account?.meetings || []).find(matches);
            if (reusedMcRoom) mtg = account.meetings.find(matches);
            return {
                eventId: e.id,
                eventTitle: e.title,
                eventType: type,
                courseId: e.courseId || e.resource?.courseId,
                startMin, endMin,
                effectiveStartMin: startMin,
                effectiveEndMin,
                startLabel: fmtTimeLabel(startMin),
                endLabel: fmtTimeLabel(endMin),
                accountId: accountId || null,
                accountName: account?.name || ef(e, 'zoomAccountName') || (accountId ? '(cuenta no encontrada)' : '⚠️ Sin cuenta'),
                accountEmail: account?.email || '',
                accountColor: account?.color || (accountId ? '#6b7280' : '#ef4444'),
                meetingId: mtg?.id || null,
                meetingName: mtg?.name || '',
                zoomLink: zoomLink || '',
                zoomMeetingId: zoomMeetingId || '',
                zoomPassword: zoomPassword || '',
                needsZoomAssignment: !!(e.needsZoomAssignment ?? e.resource?.needsZoomAssignment),
                ...(reusedMcRoom ? { reusedMcRoom: true } : {}),
            };
        });
    }, [events, zoomAccounts]);

    // Carga la vista del modal para una fecha: muestra la distribución guardada si existe.
    const loadZoomDateView = (dateStr) => {
        const existing = buildExistingZoomResult(dateStr);
        setZoomDistResult(existing);
        setZoomDistIsExisting(!!existing);
        setZoomDistWarnings([]);
        // Solo arrancan bloqueados los eventos que ya tienen cuenta: los que no, quedan listos para asignarse.
        setLockedEvents(existing ? new Set(existing.filter(a => a.accountId).map(a => a.eventId)) : new Set());
        setManualNoZoomEvents(
            existing ? new Set(existing.filter(a => a.needsZoomAssignment).map(a => a.eventId)) : new Set()
        );
    };

    const runZoomDistribute = () => {
        const result = distributeZoom(zoomDistDate, lockedEvents, zoomDistResult);
        setZoomDistResult(result);
        setZoomDistIsExisting(false);
        setZoomRecalcConfirm(false);
        // Refrescar manualNoZoomEvents desde el nuevo result (los flags persistidos).
        if (result) {
            setManualNoZoomEvents(new Set(result.filter(a => a.needsZoomAssignment).map(a => a.eventId)));
        }
    };

    const missingRoomItems = useMemo(() => missingRoomItemsFrom(zoomDistWarnings), [zoomDistWarnings]);

    // items: salas a crear (por defecto las faltantes del día mostrado; el Periodo pasa las suyas).
    const handleCreateMissingRooms = async (items = missingRoomItems) => {
        if (!items.length) return;
        setCreatingRooms(true);
        accountsAtCreateRef.current = zoomAccounts;
        try {
            const { results } = await createMissingZoomRooms(items);
            const ok = results.filter(r => r.ok).length;
            const failed = results.filter(r => !r.ok);
            if (ok) toast.success(`${ok} sala(s) listas en Zoom`);
            const byError = new Map();
            failed.forEach(f => byError.set(f.error, (byError.get(f.error) || 0) + 1));
            byError.forEach((count, error) => toast.error(`${count} sala(s): ${error}`, { duration: 7000 }));
            // Si se creó alguna sala, esperar a que zoomAccounts se actualice (onSnapshot) y redistribuir.
            // Si todas ya existían no se escribe nada: redistribuir directo.
            const fromDay = zoomDistTab === 'day' && zoomDistResult;
            if (fromDay && results.some(r => r.ok && r.created)) setRedistributePending(true);
            else if (fromDay && ok) runZoomDistribute();
        } catch (err) {
            toast.error(zoomErrorMessage(err), { duration: 7000 });
        } finally { setCreatingRooms(false); }
    };

    useEffect(() => {
        if (!redistributePending || zoomAccounts === accountsAtCreateRef.current) return;
        setRedistributePending(false);
        runZoomDistribute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redistributePending, zoomAccounts]);

    // Regla de reuso de salas de MC: se guarda con los Ajustes de creación del workspace.
    const updateDistOptions = async (patch) => {
        const ws = getCalendarWorkspaceId();
        if (!ws || !roomSettings) return;
        setRoomSettings(prev => ({ ...prev, ...patch }));
        try {
            const current = await getZoomRoomSettings(ws);
            await saveZoomRoomSettings(ws, { ...current, ...patch });
        } catch (err) {
            console.error(err);
            toast.error('No se pudo guardar la regla');
        }
    };

    const handleZoomDistribute = () => {
        // Si lo mostrado es una distribución ya guardada, confirmar antes de reemplazarla.
        if (zoomDistIsExisting) {
            setZoomRecalcConfirm(true);
            return;
        }
        runZoomDistribute();
    };

    const toggleManualNoZoom = async (assignment) => {
        const eventId = assignment.eventId;
        const newValue = !manualNoZoomEvents.has(eventId);
        // Update Set
        setManualNoZoomEvents(prev => {
            const next = new Set(prev);
            if (newValue) next.add(eventId);
            else next.delete(eventId);
            return next;
        });
        // Reflect in current result for immediate UI feedback
        setZoomDistResult(prev => prev ? prev.map(a =>
            a.eventId === eventId ? { ...a, needsZoomAssignment: newValue } : a
        ) : prev);
        // Persist to Firestore
        try {
            await updateSchedule(eventId, { needsZoomAssignment: newValue });
        } catch (err) {
            console.error('Error al persistir needsZoomAssignment:', err);
            toast.error('No se pudo guardar el estado "Sin Zoom"');
            // Revert
            setManualNoZoomEvents(prev => {
                const next = new Set(prev);
                if (newValue) next.delete(eventId);
                else next.add(eventId);
                return next;
            });
            setZoomDistResult(prev => prev ? prev.map(a =>
                a.eventId === eventId ? { ...a, needsZoomAssignment: !newValue } : a
            ) : prev);
        }
    };

    // Guarda asignaciones (de un día o de un periodo): link/ID/clave en cada evento y, por curso y
    // tipo, el primer link asignado en las variables del curso (Generador). Devuelve el resumen.
    const saveZoomAssignments = async (assignments) => {
        const valid = assignments.filter(a => a.accountId);
        const schedulePromises = valid.map(a => updateSchedule(a.eventId, a.eventType === 'masterclass'
            ? { zoomAccountId: a.accountId, zoomAccountName: a.accountName, mcLiveUrl: a.zoomLink, mcMeetingId: a.zoomMeetingId, mcPassword: a.zoomPassword }
            : { zoomAccountId: a.accountId, zoomAccountName: a.accountName, claseLiveUrl: a.zoomLink, claseMeetingId: a.zoomMeetingId, clasePassword: a.zoomPassword }));

        const courseBuckets = new Map();
        valid.forEach(a => {
            if (!a.courseId || !a.zoomLink) return;
            if (!courseBuckets.has(a.courseId)) courseBuckets.set(a.courseId, {});
            const bucket = courseBuckets.get(a.courseId);
            if (a.eventType === 'masterclass' && !bucket.mc) {
                bucket.mc = { zoomLink: a.zoomLink, meetingId: a.zoomMeetingId, password: a.zoomPassword, _zoomAccountName: a.accountName, _zoomMeetingName: a.meetingName };
            } else if (a.eventType !== 'masterclass' && !bucket.cl && !a.reusedMcRoom) {
                // Una clase en sala de MC prestada no cambia la sala de clase del curso.
                bucket.cl = { claseLiveUrl: a.zoomLink, claseMeetingId: a.zoomMeetingId, clasePassword: a.zoomPassword, _claseMeetingName: a.meetingName, _claseAccountName: a.accountName };
            }
        });
        const coursePromises = [...courseBuckets.entries()].map(([courseId, bucket]) => {
            const existing = courses.find(c => c.id === courseId);
            if (!existing) return Promise.resolve(); // curso huérfano
            const updates = {};
            if (bucket.mc) updates.defaultVariables = { ...(existing.defaultVariables || {}), ...bucket.mc };
            if (bucket.cl) updates.claseVariables = { ...(existing.claseVariables || {}), ...bucket.cl };
            return Object.keys(updates).length ? updateCourse(courseId, updates) : Promise.resolve();
        });

        const results = await Promise.allSettled(schedulePromises);
        const courseResults = await Promise.allSettled(coursePromises);
        courseResults.filter(r => r.status === 'rejected').forEach(r => console.warn('No se pudo sincronizar curso:', r.reason?.message || r.reason));
        return {
            saved: results.filter(r => r.status === 'fulfilled').length,
            failed: results.filter(r => r.status === 'rejected').length,
            courses: courseBuckets.size,
        };
    };

    const handleZoomSave = async () => {
        if (!zoomDistResult) return;
        setZoomDistSaving(true);
        try {
            const r = await saveZoomAssignments(zoomDistResult);
            if (r.failed) toast(`Zoom distribuido: ${r.saved} eventos guardados, ${r.failed} con error (ver consola)`, { icon: '⚠️' });
            else toast.success(`Zoom distribuido: ${r.saved} eventos, ${r.courses} cursos sincronizados`);
            setShowZoomDist(false);
            setZoomDistResult(null);
            setZoomDistIsExisting(false);
            fetchData();
        } catch (err) {
            toast.error('Error al guardar distribución');
            console.error(err);
        }
        setZoomDistSaving(false);
    };

    // Cerrar el modal: si hay una distribución nueva sin guardar, confirmar antes de descartarla.
    const confirmDiscardDay = () => !(zoomDistResult && !zoomDistIsExisting) || window.confirm('Hay una distribución del día sin guardar. ¿Descartarla?');
    const closeZoomDist = () => {
        if (!confirmDiscardDay()) return;
        setRedistributePending(false);
        setShowZoomDist(false);
    };

    const DAY_NAMES_ES = { monday: 'lunes', tuesday: 'martes', wednesday: 'miércoles', thursday: 'jueves', friday: 'viernes', saturday: 'sábados', sunday: 'domingos' };

    const checkRowAlert = (date, startTime, endTime, rowId, override = false) => {
        if (!date) return '';

        if (!gridInstructorId) return '';
        const inst = instructors.find(i => i.id === gridInstructorId);
        if (!inst) return '';

        // Check day availability
        const dayOfWeek = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const avail = inst.availability?.[dayOfWeek];
        if (!avail || avail.length === 0) {
            if (override) return `✅ Coordinado: ${DAY_NAMES_ES[dayOfWeek] || dayOfWeek} (omitido)`;
            return `⛔ No trabaja los ${DAY_NAMES_ES[dayOfWeek] || dayOfWeek}`;
        }

        // Check blocked days
        const blocked = inst.diasBloqueados?.find(b => b.fecha === date);
        if (blocked) {
            if (override) return `✅ Coordinado: ${blocked.motivo || 'bloqueado'} (omitido)`;
            return `⛔ Bloqueado: ${blocked.motivo || 'No disponible'}`;
        }

        // All events for this instructor on this date
        const instDayEvents = events.filter(e => {
            const eDate = toLocalDateStr(e.start);
            return eDate === date && e.resource?.instructorId === gridInstructorId;
        });

        // Check max events per day for instructor
        if (instDayEvents.length >= (inst.maxEventosDia || 5)) return `⚠️ Ya tiene ${instDayEvents.length} eventos (máx ${inst.maxEventosDia || 5})`;

        // Check time conflicts with existing calendar events (same instructor)
        if (startTime && endTime) {
            const newStart = parseInt(startTime.replace(':', ''));
            const newEnd = parseInt(endTime.replace(':', ''));
            for (const ev of instDayEvents) {
                const evStart = new Date(ev.start);
                const evEnd = new Date(ev.end);
                const evStartN = evStart.getHours() * 100 + evStart.getMinutes();
                const evEndN = evEnd.getHours() * 100 + evEnd.getMinutes();
                if (newStart < evEndN && newEnd > evStartN) {
                    // Suggest next free slot
                    const freeHour = String(evEnd.getHours()).padStart(2, '0') + ':' + String(evEnd.getMinutes()).padStart(2, '0');
                    return `⛔ Cruce con "${ev.title}" (${String(evStart.getHours()).padStart(2, '0')}:${String(evStart.getMinutes()).padStart(2, '0')}-${String(evEnd.getHours()).padStart(2, '0')}:${String(evEnd.getMinutes()).padStart(2, '0')}). Sugerencia: ${freeHour}`;
                }
            }
        }

        // Check conflicts between rows in the grid itself (same instructor, same date)
        if (startTime && endTime) {
            const newStart = parseInt(startTime.replace(':', ''));
            const newEnd = parseInt(endTime.replace(':', ''));
            for (const other of gridRows) {
                if (other.id === rowId || other.date !== date || !other.startTime || !other.endTime) continue;
                const otherStart = parseInt(other.startTime.replace(':', ''));
                const otherEnd = parseInt(other.endTime.replace(':', ''));
                if (newStart < otherEnd && newEnd > otherStart) {
                    const suggestHour = other.endTime;
                    return `⛔ Cruce con fila #${gridRows.indexOf(other) + 1} (${other.startTime}-${other.endTime}). Sugerencia: ${suggestHour}`;
                }
            }
        }

        // ── Asesor daily capacity check ──
        const thisRowType = gridRows.find(r => r.id === rowId)?.type || 'masterclass';
        if (thisRowType === 'masterclass' && asesores.length > 0) {
            const totalDailyCapacity = asesores.reduce((sum, a) => sum + (a.asesorConfig?.maxMcPerDay ?? 2), 0);

            const savedMCsOnDate = events.filter(e => {
                const eDate = toLocalDateStr(e.start);
                return eDate === date && (e.resource?.type || e.type) === 'masterclass';
            }).length;

            const gridMCsOnDate = gridRows.filter(r =>
                r.id !== rowId && r.date === date && r.type === 'masterclass'
            ).length;

            const totalMCsIfAdded = savedMCsOnDate + gridMCsOnDate + 1;

            if (totalMCsIfAdded > totalDailyCapacity) {
                return `⚠️ Sin capacidad de asesores: ${totalMCsIfAdded} MC el ${date} pero cupo total es ${totalDailyCapacity} (${asesores.length} asesores × max MC/día)`;
            }
        }

        return '';
    };

    const getDefaultTimes = (type) => {
        const course = courses.find(c => c.id === gridCourseId);
        if (!course) return { startTime: '18:00', endTime: '20:00' };
        if (type === 'masterclass') {
            const start = course.masterclassTime || '18:00';
            const hours = Number(course.masterclassHours) || 1;
            const [h, m] = start.split(':').map(Number);
            const totalMin = h * 60 + m + Math.round(hours * 60);
            const endH = Math.floor(totalMin / 60) % 24;
            const endM = totalMin % 60;
            return { startTime: start, endTime: `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}` };
        } else {
            const start = course.sessionTime || '20:00';
            const hours = Number(course.regularHours) || 2;
            const [h, m] = start.split(':').map(Number);
            const totalMin = h * 60 + m + Math.round(hours * 60);
            const endH = Math.floor(totalMin / 60) % 24;
            const endM = totalMin % 60;
            return { startTime: start, endTime: `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}` };
        }
    };

    // Existing events for selected course+instructor
    const existingCourseEvents = useMemo(() => {
        if (!gridCourseId || !gridInstructorId) return [];
        return events
            .filter(e => (e.resource?.courseId || e.courseId) === gridCourseId &&
                         (e.resource?.instructorId || e.instructorId) === gridInstructorId &&
                         e.status !== 'cancelled')
            .sort((a, b) => new Date(a.start) - new Date(b.start));
    }, [events, gridCourseId, gridInstructorId]);

    const existingGroups = useMemo(() => {
        const groups = new Set(existingCourseEvents.map(e => e.groupNumber || e.resource?.groupNumber || 1));
        return [...groups].sort((a, b) => a - b);
    }, [existingCourseEvents]);

    // Grupos "activos" = los que tienen al menos UNA fecha de hoy en adelante.
    // Editar carga TODAS las fechas de esos grupos (pasadas y futuras); los grupos
    // totalmente vencidos quedan excluidos y protegidos.
    const editableExistingEvents = useMemo(() => {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const activeGroups = new Set(
            existingCourseEvents
                .filter(e => new Date(e.start) >= startOfToday)
                .map(e => e.groupNumber || 1)
        );
        return existingCourseEvents.filter(e => activeGroups.has(e.groupNumber || 1));
    }, [existingCourseEvents]);

    // Reset mode when course/instructor changes
    useEffect(() => {
        if (gridCourseId && gridInstructorId) {
            if (existingCourseEvents.length > 0) {
                // Por defecto: nuevo grupo (la barra compacta permite cambiar de acción)
                const maxG = existingGroups.length > 0 ? Math.max(...existingGroups) : 1;
                setGridMode('new');
                setExistingGroupNum(maxG + 1);
            } else {
                setGridMode('new'); // No existing events, go straight to new
                setExistingGroupNum(1);
            }
        } else {
            setGridMode(null);
        }
    }, [gridCourseId, gridInstructorId]);

    const handleSelectMode = (mode) => {
        dirtyRef.current = false;
        setGridMode(mode);
        if (mode === 'edit') {
            // Load only ACTIVE existing events as editable rows, ordered by group then date
            const rows = editableExistingEvents
                .slice()
                .sort((a, b) => {
                    const ga = a.groupNumber || 1, gb = b.groupNumber || 1;
                    if (ga !== gb) return ga - gb;
                    return new Date(a.start) - new Date(b.start);
                })
                .map(ev => {
                const start = new Date(ev.start);
                const end = new Date(ev.end);
                return {
                    id: ev.id,
                    _existingEventId: ev.id, // Mark as existing
                    type: (ev.resource?.type || ev.type) || 'masterclass',
                    date: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
                    startTime: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
                    endTime: `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
                    alert: '',
                    override: false,
                    groupNumber: ev.groupNumber || 1,
                };
            });
            setGridRows(rows.length > 0 ? rows : [{ id: Date.now(), type: 'masterclass', date: '', ...getDefaultTimes('masterclass'), alert: '', override: false }]);
        } else if (mode === 'add') {
            const maxGroup = existingGroups.length > 0 ? Math.max(...existingGroups) : 1;
            setExistingGroupNum(maxGroup);
            const defaults = getDefaultTimes('masterclass');
            setGridRows([{ id: Date.now(), type: 'masterclass', date: '', startTime: defaults.startTime, endTime: defaults.endTime, alert: '', override: false }]);
        } else if (mode === 'new') {
            const maxGroup = existingGroups.length > 0 ? Math.max(...existingGroups) + 1 : 1;
            setExistingGroupNum(maxGroup);
            const defaults = getDefaultTimes('masterclass');
            setGridRows([{ id: Date.now(), type: 'masterclass', date: '', startTime: defaults.startTime, endTime: defaults.endTime, alert: '', override: false }]);
        }
    };

    // Update empty rows when course changes to use course defaults
    useEffect(() => {
        if (!gridCourseId) return;
        setGridRows(prev => prev.map(row => {
            if (row.date) return row; // Don't change rows with dates already set
            const defaults = getDefaultTimes(row.type);
            return { ...row, startTime: defaults.startTime, endTime: defaults.endTime };
        }));
    }, [gridCourseId]);

    const addGridRow = () => {
        const defaults = getDefaultTimes('masterclass');
        dirtyRef.current = true;
        setGridRows(prev => [...prev, { id: Date.now(), type: 'masterclass', date: '', startTime: defaults.startTime, endTime: defaults.endTime, alert: '', override: false }]);
    };

    const updateGridRow = (id, field, value) => {
        dirtyRef.current = true;
        setGridRows(prev => {
            const updated = prev.map(row => {
                if (row.id !== id) return row;
                const newRow = { ...row, [field]: value };
                // Reset override if date changes
                if (field === 'date') newRow.override = false;
                // Update default times when type changes
                if (field === 'type') {
                    const defaults = getDefaultTimes(value);
                    newRow.startTime = defaults.startTime;
                    newRow.endTime = defaults.endTime;
                }
                return newRow;
            });
            // Re-check alerts for all rows (cross-row conflicts)
            return updated.map(row => ({
                ...row,
                alert: row.date ? checkRowAlert(row.date, row.startTime, row.endTime, row.id, row.override) : ''
            }));
        });
    };

    const toggleRowOverride = (id) => {
        dirtyRef.current = true;
        setGridRows(prev => {
            const updated = prev.map(row => {
                if (row.id !== id) return row;
                return { ...row, override: !row.override };
            });
            return updated.map(row => ({
                ...row,
                alert: row.date ? checkRowAlert(row.date, row.startTime, row.endTime, row.id, row.override) : ''
            }));
        });
    };

    const removeGridRow = (id) => {
        dirtyRef.current = true;
        setGridRows(prev => prev.filter(row => row.id !== id));
    };

    // Re-check alerts when instructor changes
    useEffect(() => {
        if (!gridInstructorId) return;
        setGridRows(prev => prev.map(row => ({ ...row, alert: row.date ? checkRowAlert(row.date, row.startTime, row.endTime, row.id) : '' })));
    }, [gridInstructorId]);

    const handleInsertGrid = async () => {
        if (!gridInstructorId) { toast.error('Selecciona un instructor'); return; }
        const validRows = gridRows.filter(r => r.date && r.startTime && r.endTime);
        if (validRows.length === 0) { toast.error('Agrega al menos una fecha'); return; }
        const blockedRows = validRows.filter(r => r.alert?.startsWith('⛔') && !r.override);
        if (blockedRows.length > 0) { toast.error(`${blockedRows.length} fila(s) tienen errores. Corrígelas o usa "Omitir".`); return; }
        const warningRows = validRows.filter(r => r.alert?.startsWith('⚠️'));
        if (warningRows.length > 0 && !window.confirm(`${warningRows.length} advertencia(s). ¿Continuar?`)) return;

        const inst = instructors.find(i => i.id === gridInstructorId);
        const course = courses.find(c => c.id === gridCourseId);

        setIsInsertingGrid(true);
        try {
            let created = 0, updated = 0, deleted = 0;

            // Helper: parse date+time safely (handles both yyyy-mm-dd and dd/mm/yyyy)
            const makeDate = (dateStr, timeStr) => {
                let y, m, d;
                if (dateStr.includes('-')) {
                    [y, m, d] = dateStr.split('-').map(Number);
                } else if (dateStr.includes('/')) {
                    [d, m, y] = dateStr.split('/').map(Number);
                } else {
                    return new Date(dateStr + 'T' + timeStr + ':00');
                }
                const [h, min] = timeStr.split(':').map(Number);
                return new Date(y, m - 1, d, h, min, 0);
            };

            if (gridMode === 'edit') {
                // EDIT MODE: Update existing, insert new, delete removed
                const existingIds = new Set(existingCourseEvents.map(e => e.id));
                const currentIds = new Set(validRows.filter(r => r._existingEventId).map(r => r._existingEventId));

                // Count deletions and confirm
                // Solo se pueden borrar fechas activas cargadas; nunca eventos vencidos.
                const toDelete = editableExistingEvents.filter(ev => !currentIds.has(ev.id));
                if (toDelete.length > 0 && !window.confirm(`Se eliminarán ${toDelete.length} evento(s) del calendario. ¿Continuar?`)) {
                    setIsInsertingGrid(false);
                    return;
                }

                // Delete removed events
                for (const ev of toDelete) {
                    await deleteSchedule(ev.id);
                    deleted++;
                }

                // Update or create
                for (const row of validRows) {
                    const typeLabel = row.type === 'masterclass' ? 'MC' : 'Clase';
                    const title = course ? `${typeLabel} ${course.name}` : `${typeLabel} - ${inst?.name || ''}`;
                    if (row._existingEventId && existingIds.has(row._existingEventId)) {
                        // Update existing
                        await updateSchedule(row._existingEventId, {
                            type: row.type,
                            title,
                            start: makeDate(row.date, row.startTime),
                            end: makeDate(row.date, row.endTime),
                            groupNumber: row.groupNumber || existingGroupNum,
                        });
                        updated++;
                    } else {
                        // Insert new
                        await createSchedule({
                            type: row.type, title, instructorId: gridInstructorId,
                            courseId: gridCourseId || null, groupNumber: row.groupNumber || existingGroupNum,
                            start: makeDate(row.date, row.startTime),
                            end: makeDate(row.date, row.endTime),
                            notes: '', color: inst?.color || '#3b82f6', status: 'confirmed', createdManually: true,
                        });
                        created++;
                    }
                }
                const parts = [];
                if (updated) parts.push(`${updated} actualizado${updated > 1 ? 's' : ''}`);
                if (created) parts.push(`${created} nuevo${created > 1 ? 's' : ''}`);
                if (deleted) parts.push(`${deleted} eliminado${deleted > 1 ? 's' : ''}`);
                toast.success(parts.join(', ') || 'Sin cambios');

            } else {
                // ADD or NEW mode
                const groupNum = existingGroupNum;
                for (const row of validRows) {
                    if (row._existingEventId) continue; // Skip existing rows
                    const typeLabel = row.type === 'masterclass' ? 'MC' : 'Clase';
                    const title = course ? `${typeLabel} ${course.name}` : `${typeLabel} - ${inst?.name || ''}`;
                    await createSchedule({
                        type: row.type, title, instructorId: gridInstructorId,
                        courseId: gridCourseId || null, groupNumber: groupNum,
                        start: makeDate(row.date, row.startTime),
                        end: makeDate(row.date, row.endTime),
                        notes: '', color: inst?.color || '#3b82f6', status: 'confirmed', createdManually: true,
                    });
                    created++;
                }
                toast.success(`${created} evento${created > 1 ? 's' : ''} insertado${created > 1 ? 's' : ''}`);
            }
            if (gridInstructorId) {
                const shareUrl = `${window.location.origin}/horario/${gridInstructorId}`;
                navigator.clipboard.writeText(shareUrl).then(() => {
                    toast.success('Enlace de programación copiado al portapapeles', { duration: 4000, icon: '🔗' });
                }).catch(() => { });
            }
            clearDraft();
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error('Error al insertar eventos');
        }
        setIsInsertingGrid(false);
    };

    // Popover State
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 });
    // Inline edit for agenda instructor (no popup)
    const [agendaEditEvent, setAgendaEditEvent] = useState(null);

    // AI Pattern Analysis State
    const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [expandedSection, setExpandedSection] = useState('cursos');
    const [savedMemory, setSavedMemory] = useState(() => loadMemory());

    // AI Chat State
    const [isChatOpen, setIsChatOpen] = useState(false);

    // GCal Import State
    const [isGCalImportOpen, setIsGCalImportOpen] = useState(false);
    const [showGCalExport, setShowGCalExport] = useState(false);

    // Filtered events based on search term + hidden status
    const filteredEvents = useMemo(() => {
        let filtered = events.filter(e => !e.hidden && !e.resource?.hidden);
        // Modo instructor: limitar a los eventos de ese instructor.
        if (instructorMode && viewerInstructorId) {
            filtered = filtered.filter(e => (e.instructorId || e.resource?.instructorId) === viewerInstructorId);
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(event =>
                event.title?.toLowerCase().includes(term) ||
                event.type?.toLowerCase().includes(term) ||
                instructors.find(i => i.id === event.instructorId)?.name?.toLowerCase().includes(term) ||
                courses.find(c => c.id === event.courseId)?.name?.toLowerCase().includes(term)
            );
        }
        // Expose schedules for CoursesPage MC/CL count
        window.__calSchedules = events.map(e => ({ courseId: e.courseId || e.resource?.courseId, type: e.type || e.resource?.type }));
        return filtered;
    }, [events, searchTerm, instructors, courses, instructorMode, viewerInstructorId]);

    // Merge internal events with Google Calendar events
    const mergedEvents = useMemo(() => {
        if (!gcalConnected || googleEvents.length === 0) return filteredEvents;
        return [...filteredEvents, ...googleEvents];
    }, [filteredEvents, gcalConnected, googleEvents]);

    // Posición de cada evento en su secuencia de curso (Clase 1 de 6, etc.),
    // usada para enriquecer el título de la descripción del evento.
    const eventEnumCache = useMemo(() => buildEnumCache(mergedEvents, courses), [mergedEvents, courses]);

    useEffect(() => {
        fetchData();
    }, [instructorMode, viewerInstructorId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync Google Calendar events when connected or visible range changes
    useEffect(() => {
        if (gcalConnected) {
            syncGoogleEvents(visibleRange.start, visibleRange.end);
        }
    }, [gcalConnected, visibleRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        setLoading(true);
        try {
            // Initialize workspace for multi-tenant filtering
            if (user?.uid) await setCalendarWorkspace(user.uid);

            let evts, insts, crs, asrs;
            if (instructorMode && viewerInstructorId) {
                // Vista de instructor: cargar SOLO sus eventos por instructorId
                // (lectura global, sin filtro de workspace) porque su cuenta puede
                // vivir en otro workspace que el de la academia. Así SÍ ve su cronograma
                // y sigue limitado a lo suyo (privado).
                evts = (await getSchedulesByInstructor(viewerInstructorId)).filter(e => e.hidden !== true);
                const courseIds = [...new Set(evts.map(e => e.courseId).filter(Boolean))];
                crs = await getCoursesByIds(courseIds);
                insts = [];
                asrs = [];
            } else {
                [evts, insts, crs, asrs] = await Promise.all([
                    getSchedules(),
                    getInstructors(),
                    getCourses(),
                    getAsesores()
                ]);
            }
            setAsesores(asrs);

            // Load users for cancel modal assignee selector
            try {
                const { collection: col, getDocs: gd } = await import('firebase/firestore');
                const { db: fdb } = await import('../../../services/firebase');
                const usersSnap = await gd(col(fdb, 'users'));
                setAllUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.status === 'active'));
            } catch { }

            // Map events to big-calendar format
            const formattedEvents = evts.map(e => {
                const course = crs.find(c => c.id === e.courseId);
                return {
                    ...e,
                    start: e.start instanceof Date ? e.start : new Date(e.start),
                    end: e.end instanceof Date ? e.end : new Date(e.end),
                    title: e.title || (insts.find(i => i.id === e.instructorId)?.name + ': ' + e.type),
                    color: e.color || insts.find(i => i.id === e.instructorId)?.color,
                    courseAbreviatura: course?.abreviatura || null,
                };
            });

            setEvents(formattedEvents);
            setInstructors(insts);
            setCourses(crs);
        } catch (error) {
            console.error(error);
            toast.error('Error cargando datos del calendario');
        } finally {
            setLoading(false);
        }
    };

    const moveEvent = useCallback(async ({ event, start, end }) => {
        // Optimistic Update
        const updatedEvent = { ...event, start, end };
        setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));

        try {
            await updateSchedule(event.id, {
                start,
                end
            });
            toast.success('Horario actualizado');
        } catch (error) {
            console.error(error);
            toast.error('Error al mover evento');
            fetchData(); // Rollback
        }
    }, []);

    const resizeEvent = useCallback(async ({ event, start, end }) => {
        const updatedEvent = { ...event, start, end };
        setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));

        try {
            await updateSchedule(event.id, { start, end });
            toast.success('Duración actualizada');
        } catch (error) {
            toast.error('Error al redimensionar');
            fetchData();
        }
    }, []);

    const handleGenerateAI = async () => {
        if (instructors.length === 0 || courses.length === 0) {
            toast.error('Debes tener instructores y CURSOS creados');
            return;
        }

        setIsGenerating(true);
        try {
            // Obtener configuración del sistema
            const config = await getCalendarConfig();

            // If regenerate mode and specific course, delete old events first
            if (regenerateMode && selectedCourseId) {
                const eventsToDelete = events.filter(e =>
                    e.courseId === selectedCourseId &&
                    new Date(e.start).getMonth() + 1 === targetMonth &&
                    new Date(e.start).getFullYear() === targetYear
                );

                if (eventsToDelete.length > 0) {
                    toast.loading(`Eliminando ${eventsToDelete.length} eventos antiguos...`);
                    for (const event of eventsToDelete) {
                        await deleteSchedule(event.id);
                    }
                    toast.dismiss();
                }
            }

            // Filter courses if specific course selected
            const coursesToGenerate = selectedCourseId
                ? courses.filter(c => c.id === selectedCourseId)
                : courses;

            const preferences = {
                modoSistema: config.modoSistema || 'expansion',
                maxMasterclassDiario: config.maxMasterclassDiario || 5,
                maxEventosDiario: config.maxEventosDiario || 10,
                masterclassCount: masterclassCount // Use manual count instead of asesor-based
            };

            // Prepare Google Calendar events for AI conflict avoidance
            let gcalEventsForAI = [];
            if (gcalConnected && googleEvents.length > 0) {
                const monthStart = new Date(targetYear, targetMonth - 1, 1);
                const monthEnd = new Date(targetYear, targetMonth, 0, 23, 59, 59);
                const relevantGcalEvents = googleEvents.filter(e => {
                    const start = new Date(e.start);
                    return start >= monthStart && start <= monthEnd;
                });
                gcalEventsForAI = simplifyGoogleEventsForAI(relevantGcalEvents);
            }

            // 1. Call Gemini with selected courses, manual MC count, and Google Calendar events
            const generatedEvents = await generateSchedule(instructors, coursesToGenerate, targetMonth, targetYear, preferences, gcalEventsForAI);

            // 2. Validate and format Dates
            const eventsWithDates = generatedEvents.map(e => ({
                ...e,
                start: new Date(e.start),
                end: new Date(e.end),
                status: 'draft' // Mark as draft
            }));

            // 3. Save to DB (or just preview state? Let's save for now as drafts)
            await saveGeneratedSchedule(eventsWithDates);

            toast.success('¡Horario Generado con Éxito! 🤖');
            setIsAiModalOpen(false);
            fetchData(); // Refresh UI

        } catch (error) {
            console.error(error);
            toast.error('Error generando horario: ' + error.message);
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle calendar range change (month/week navigation)
    const handleRangeChange = useCallback((range) => {
        let start, end;
        if (Array.isArray(range)) {
            start = range[0];
            end = range[range.length - 1];
        } else if (range.start && range.end) {
            start = range.start;
            end = range.end;
        }
        if (start && end) {
            setVisibleRange({ start, end });
        }
    }, []);

    // Preferencia por usuario: cómo se abre el detalle del evento ('popup' | 'column')
    const eventView = user?.preferences?.calendarEventView === 'column' ? 'column' : 'popup';
    const handleToggleEventView = async () => {
        const next = eventView === 'popup' ? 'column' : 'popup';
        try {
            const { doc: fdoc, setDoc: fSetDoc } = await import('firebase/firestore');
            const { db: fdb } = await import('../../../services/firebase');
            await fSetDoc(fdoc(fdb, 'users', user.uid), { preferences: { calendarEventView: next } }, { merge: true });
        } catch (err) {
            console.error('Error guardando preferencia de vista del evento', err);
            toast.error('No se pudo guardar la preferencia');
        }
    };

    // Plantilla de descripción editable por usuario.
    const descriptionTemplate = user?.preferences?.calendarEventTemplate || '';
    const handleSaveEventTemplate = async (tpl) => {
        try {
            const { doc: fdoc, setDoc: fSetDoc } = await import('firebase/firestore');
            const { db: fdb } = await import('../../../services/firebase');
            await fSetDoc(fdoc(fdb, 'users', user.uid), { preferences: { calendarEventTemplate: tpl } }, { merge: true });
            toast.success('Plantilla guardada');
        } catch (err) {
            console.error('Error guardando plantilla de evento', err);
            toast.error('No se pudo guardar la plantilla');
        }
    };

    // Popover Handlers
    const handleSelectEvent = (event, e) => {
        // Modo instructor: solo lectura → abre el detalle/solicitud de reprogramación en el contenedor.
        if (instructorMode) {
            const course = courses.find(c => c.id === (event.courseId || event.resource?.courseId)) || null;
            onInstructorEventClick?.(event, course);
            return;
        }
        const x = e?.clientX || window.innerWidth / 2;
        const y = e?.clientY || window.innerHeight / 2;
        setPopoverPos({ x, y });
        setSelectedEvent(event);
    };

    const handleSavePopover = async (id, data) => {
        setEvents(prev => prev.map(evt => evt.id === id ? { ...evt, ...data } : evt));
        setSelectedEvent(null);
        try {
            await updateSchedule(id, data);
            toast.success('Evento actualizado');
        } catch (error) {
            toast.error('Error al guardar');
            fetchData();
        }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm("¿Eliminar este evento?")) return;
        setEvents(prev => prev.filter(evt => evt.id !== id));
        setSelectedEvent(null);
        try {
            await deleteSchedule(id);
            toast.success("Evento eliminado");
        } catch (error) {
            toast.error("Error al eliminar");
            fetchData();
        }
    };

    // Cancel: open modal to select assignee (or reactivate directly)
    const handleCancelEvent = async (id, reactivate = false) => {
        if (reactivate) {
            try {
                await updateSchedule(id, { status: 'confirmed', cancelledAt: null, reprogramTaskId: null });
                setEvents(prev => prev.map(e => e.id === id ? { ...e, status: 'confirmed' } : e));
                setSelectedEvent(null);
                toast.success('Evento reactivado');
            } catch { toast.error('Error'); }
            return;
        }
        // Open cancel modal with assignee selector
        const evt = events.find(e => e.id === id);
        setCancelModal({ eventId: id, event: evt });
        setCancelAssignee('');
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setCancelDueDate(toLocalDateStr(tomorrow));
        setSelectedEvent(null);
    };

    // Execute cancel + create task + notification
    const executeCancelWithTask = async () => {
        if (!cancelModal) return;
        const { eventId, event: evt } = cancelModal;
        if (!cancelAssignee) { toast.error('Selecciona a quién asignar'); return; }

        try {
            // 1. Cancel the schedule event
            await updateSchedule(eventId, {
                status: 'cancelled',
                cancelledAt: new Date().toISOString(),
                cancelledBy: user?.uid,
            });
            setEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: 'cancelled' } : e));

            const assigneeUser = allUsers.find(u => u.email === cancelAssignee || u.id === cancelAssignee);
            const assigneeEmail = assigneeUser?.email || cancelAssignee;
            const assigneeName = assigneeUser?.displayName || assigneeEmail;
            const evtDate = new Date(evt?.start).toLocaleDateString('es', { day: '2-digit', month: 'short' });

            // 2. Create task in Firebase (Tareas module)
            const { doc: fdoc, setDoc: fSetDoc, collection: fCol, serverTimestamp: fST } = await import('firebase/firestore');
            const { db: fdb } = await import('../../../services/firebase');

            // Find workspace
            const userDoc = await (await import('firebase/firestore')).getDoc(fdoc(fdb, 'users', user.uid));
            const workspaceId = userDoc.data()?.workspaceId || user.uid;

            // Get first project/folder/list for task creation
            const projSnap = await (await import('firebase/firestore')).getDocs(fCol(fdb, `artifacts/gestor-respuestas-pro/users/${workspaceId}/projects`));
            if (projSnap.empty) throw new Error('No hay proyectos');
            const proj = projSnap.docs[0];
            const folderSnap = await (await import('firebase/firestore')).getDocs(fCol(fdb, `artifacts/gestor-respuestas-pro/users/${workspaceId}/projects/${proj.id}/folders`));
            if (folderSnap.empty) throw new Error('No hay carpetas');
            const folder = folderSnap.docs[0];
            const listSnap = await (await import('firebase/firestore')).getDocs(fCol(fdb, `artifacts/gestor-respuestas-pro/users/${workspaceId}/projects/${proj.id}/folders/${folder.id}/lists`));
            if (listSnap.empty) throw new Error('No hay listas');
            const list = listSnap.docs[0];

            const taskId = `task-${Date.now()}`;
            const taskRef = fdoc(fdb, `artifacts/gestor-respuestas-pro/users/${workspaceId}/projects/${proj.id}/folders/${folder.id}/lists/${list.id}/items/${taskId}`);
            await fSetDoc(taskRef, {
                id: taskId,
                titulo: `Reprogramar: ${evt?.title || 'Clase'} - ${evtDate} (cancelada)`,
                descripcion: `<p>La clase <b>${evt?.title}</b> programada para el <b>${evtDate}</b> fue cancelada.</p><p>Por favor reprogramar a una nueva fecha.</p>`,
                estado: 'planificacion',
                prioridad: 'alta',
                asignados: [assigneeEmail],
                allAsignados: [assigneeEmail],
                fechaVencimiento: cancelDueDate ? new Date(cancelDueDate + 'T23:59:59').toISOString() : null,
                fechaCreacion: fST(),
                recurrence: 'none',
                subtareas: [],
                listId: list.id,
                folderId: folder.id,
                projectId: proj.id,
                creadoPor: user.uid,
                // Link to calendar event
                calendarEventId: eventId,
                calendarCourseId: evt?.resource?.courseId || evt?.courseId || null,
                updatedAt: fST(),
            });

            // Save task reference on the schedule
            await updateSchedule(eventId, { reprogramTaskId: taskId });

            // 3. Create notification (global + assigned user)
            await (await import('firebase/firestore')).addDoc(fCol(fdb, 'notifications'), {
                type: 'reprogramacion_pendiente',
                title: 'Clase cancelada - Reprogramar',
                body: `${evt?.title} (${evtDate}) cancelada. Asignada a ${assigneeName}. Vence: ${cancelDueDate}`,
                eventId,
                courseId: evt?.resource?.courseId || evt?.courseId || null,
                read: false,
                global: true,
                createdAt: fST(),
                createdBy: user?.uid,
                assignedTo: assigneeEmail,
            });

            setCancelModal(null);
            toast.success(`Clase cancelada. Tarea asignada a ${assigneeName}`);
        } catch (err) {
            console.error('Cancel+task error:', err);
            toast.error('Error: ' + (err.message || 'Intenta de nuevo'));
        }
    };

    // Manual Event Creation Handler
    const handleCreateEvent = async () => {
        if (!newEvent.title.trim()) {
            toast.error('El título es requerido');
            return;
        }
        if (!newEvent.instructorId) {
            toast.error('Selecciona un instructor');
            return;
        }
        if (!newEvent.start || !newEvent.end) {
            toast.error('Las fechas son requeridas');
            return;
        }

        setIsCreating(true);
        try {
            const instructor = instructors.find(i => i.id === newEvent.instructorId);
            const course = courses.find(c => c.id === newEvent.courseId);

            const eventData = {
                type: newEvent.type,
                title: newEvent.title || `${instructor?.name}: ${newEvent.type === 'masterclass' ? 'Masterclass' : 'Clase'}`,
                instructorId: newEvent.instructorId,
                courseId: newEvent.courseId || null,
                start: new Date(newEvent.start),
                end: new Date(newEvent.end),
                notes: newEvent.notes,
                color: instructor?.color || '#6366f1',
                status: 'confirmed',
                createdManually: true
            };

            await createSchedule(eventData);
            toast.success('Evento creado correctamente');
            setIsCreateModalOpen(false);
            setNewEvent({
                type: 'masterclass',
                title: '',
                instructorId: '',
                courseId: '',
                start: '',
                end: '',
                notes: ''
            });
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error('Error al crear el evento');
        } finally {
            setIsCreating(false);
        }
    };

    // AI Pattern Analysis - analiza todos los eventos con memoria de análisis previos
    const handleAnalyzePatterns = async () => {
        const allEvents = [
            ...events,
            ...(gcalConnected ? googleEvents : [])
        ];

        if (allEvents.length === 0) {
            toast.error('No hay eventos para analizar. Carga eventos primero.');
            return;
        }

        setIsAnalyzing(true);
        setAnalysisResult(null);
        setIsAnalysisOpen(true);

        try {
            const memory = loadMemory();
            const memMsg = memory
                ? `Analizando ${allEvents.length} eventos (con memoria del ${memory.fechaAnalisis})...`
                : `Analizando ${allEvents.length} eventos por primera vez...`;
            toast.loading(memMsg, { id: 'analysis' });

            const result = await analyzeCalendarWithMemory(allEvents, memory);
            setAnalysisResult(result);

            // Guardar nuevo análisis en memoria
            saveMemory(result);
            setSavedMemory(loadMemory());

            toast.success('Análisis completado y guardado en memoria', { id: 'analysis' });
        } catch (error) {
            console.error(error);
            toast.error('Error al analizar: ' + error.message, { id: 'analysis' });
        } finally {
            setIsAnalyzing(false);
        }
    };

    // Handle events proposed by AI chat
    const handleChatCreateEvents = async (eventosProposed) => {
        let created = 0;
        for (const ev of eventosProposed) {
            try {
                const start = new Date(ev.inicio);
                const end = new Date(ev.fin);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
                await createSchedule({
                    type: ev.tipo || 'masterclass',
                    title: ev.titulo,
                    start,
                    end,
                    color: ev.color || '#6366f1',
                    status: 'draft',
                    createdByAI: true,
                });
                created++;
            } catch (err) {
                console.error('Error creando evento IA:', err);
            }
        }
        toast.success(`${created} evento${created !== 1 ? 's' : ''} creado${created !== 1 ? 's' : ''} en el calendario`);
        fetchData();
    };

    // Reset create modal
    const openCreateModal = () => {
        const now = new Date();
        const startTime = new Date(now.setHours(now.getHours() + 1, 0, 0, 0));
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

        setNewEvent({
            type: 'masterclass',
            title: '',
            instructorId: instructors[0]?.id || '',
            courseId: '',
            start: startTime.toISOString().slice(0, 16),
            end: endTime.toISOString().slice(0, 16),
            notes: ''
        });
        setIsCreateModalOpen(true);
    };

    return (
        <div className="h-full bg-gray-50 flex flex-col cal-page">
            {/* Header - Google Calendar style - single row */}
            <div className="bg-white px-3 py-1.5 border-b border-gray-200 flex items-center gap-2 z-10 shrink-0">
                {/* Navigation */}
                <button onClick={() => calNavRef.current?.('TODAY')}
                    className="px-2.5 py-1 border border-gray-300 rounded-full text-[11px] font-medium text-gray-700 hover:bg-gray-50 transition">
                    Hoy
                </button>
                {calView !== 'month' && calView !== '3semanas' && (
                    <>
                        <button onClick={() => calNavRef.current?.('PREV')} className="p-0.5 text-gray-500 hover:bg-gray-100 rounded-full"><span className="text-base">‹</span></button>
                        <button onClick={() => calNavRef.current?.('NEXT')} className="p-0.5 text-gray-500 hover:bg-gray-100 rounded-full"><span className="text-base">›</span></button>
                    </>
                )}
                <h2 className="text-sm font-semibold text-gray-800 capitalize whitespace-nowrap">{calLabel}</h2>

                {/* Search */}
                <div className="relative flex-1 max-w-[200px] ml-2">
                    <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-2 py-1 border border-gray-200 rounded-full text-[11px] focus:ring-1 focus:ring-blue-400 outline-none bg-gray-50" />
                    {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400"><FiX size={10} /></button>}
                </div>

                <div className="flex-1" />

                {/* Action buttons */}
                {/* Alerts button */}
                <button onClick={() => setShowAlerts(true)}
                    className={`p-1 rounded-full transition relative ${calendarAlerts.length > 0 ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 hover:bg-gray-100'}`}
                    title={calendarAlerts.length > 0 ? `${calendarAlerts.length} días con conflictos` : 'Alertas de capacidad'}>
                    <FiAlertTriangle size={13} />
                    {calendarAlerts.length > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-red-500 rounded-full text-[8px] text-white font-bold flex items-center justify-center border border-white">
                            {calendarAlerts.length}
                        </span>
                    )}
                </button>
                <button onClick={fetchData} className="p-1 text-gray-400 hover:bg-gray-100 rounded-full transition" title="Recargar">
                    <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </button>
                {!instructorMode && (
                    <button onClick={openCreateModal}
                        className="flex items-center gap-1 bg-white text-gray-700 border border-gray-300 px-2.5 py-1 rounded-full text-[11px] font-medium hover:bg-gray-50 transition">
                        <FiPlus size={12} /> Evento
                    </button>
                )}
                {!instructorMode && (
                    <button onClick={() => { const d = toLocalDateStr(new Date()); setZoomDistDate(d); loadZoomDateView(d); setShowZoomDist(true); }}
                        className="flex items-center gap-1 bg-white text-blue-600 border border-blue-200 px-2.5 py-1 rounded-full text-[11px] font-medium hover:bg-blue-50 transition"
                        title="Distribuir cuentas Zoom">
                        <FiExternalLink size={12} /> Zoom
                    </button>
                )}
                {(!instructorMode || viewerInstructorId) && (
                    <button onClick={() => setShowGCalExport(true)}
                        className="flex items-center gap-1 bg-white text-gray-700 border border-gray-300 px-2.5 py-1 rounded-full text-[11px] font-medium hover:bg-gray-50 transition"
                        title="Exportar a Google Calendar (suscripción que se actualiza sola)">
                        <FiCalendar size={12} /> Google
                    </button>
                )}
                <button onClick={() => setShowGrid(!showGrid)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition relative ${showGrid ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50'}`}>
                    <FiCalendar size={12} /> {instructorMode ? 'Agenda' : 'Programar'}
                    {!instructorMode && (hasDraft || savedDrafts.length > 0) && !showGrid && (
                        <span className="absolute -top-1 -right-1 min-w-[12px] h-[12px] bg-amber-500 rounded-full border border-white text-[7px] text-white font-bold flex items-center justify-center">{savedDrafts.length + (hasDraft ? 1 : 0)}</span>
                    )}
                </button>
                {!instructorMode && (
                    <button onClick={handleAnalyzePatterns} disabled={isAnalyzing}
                        className="flex items-center gap-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2.5 py-1 rounded-full text-[11px] font-medium transition disabled:opacity-50">
                        <FiZap size={12} /> IA{savedMemory && <span className="w-1 h-1 bg-green-400 rounded-full" />}
                    </button>
                )}

                {/* View dropdown */}
                <div className="relative" ref={viewMenuRef}>
                    <button onClick={() => setShowViewMenu(!showViewMenu)}
                        className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded-full text-[11px] font-medium text-gray-700 hover:bg-gray-50 transition">
                        {calViewLabel} <span className="text-[9px]">▾</span>
                    </button>
                    {showViewMenu && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50">
                            {[['month', 'Mes'], ['3semanas', '3 Semanas'], ['week', 'Semana'], ['day', 'Día'], ['agenda', 'Agenda']].map(([k, v]) => (
                                <button key={k} onClick={() => { calViewRef.current?.(k); setShowViewMenu(false); }}
                                    className={`w-full text-left px-3 py-1.5 text-xs transition ${calView === k ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                                    {v}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Calendar Area + Grid Panel */}
            <div className="flex-1 flex overflow-hidden">
                {/* Calendar */}
                <div className={`flex-1 overflow-hidden transition-all duration-300`}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="spinner text-blue-600"></div>
                        </div>
                    ) : (
                        <DragDropCalendar
                            events={mergedEvents}
                            courses={courses}
                            onEventDrop={instructorMode ? (() => {}) : moveEvent}
                            onEventResize={instructorMode ? (() => {}) : resizeEvent}
                            onSelectEvent={handleSelectEvent}
                            onRangeChange={handleRangeChange}
                            highlightInstructorId={
                                !showGrid
                                    ? null
                                    : showInstructorTable
                                        ? (filterInstructorId || null)
                                        : (gridInstructorId || null)
                            }
                            highlightCourseId={
                                showGrid && showInstructorTable
                                    ? agendaHighlightCourseId
                                    : null
                            }
                            onVisibleLabel={(lbl) => setCalLabel(lbl)}
                            initialView={savedCalPrefs?.calView || 'month'}
                            initialDate={new Date()}
                            onToolbarReady={(tb) => {
                                if (tb.label) setCalLabel(tb.label);
                                if (tb.nav) calNavRef.current = tb.nav;
                                if (tb.viewFn) calViewRef.current = tb.viewFn;
                                if (tb.view) setCalView(tb.view);
                                if (tb.viewLabel) setCalViewLabel(tb.viewLabel);
                            }}
                        />
                    )}
                    {selectedEvent && eventView === 'popup' && (
                        <EventPopover
                            event={selectedEvent}
                            position={popoverPos}
                            onClose={() => setSelectedEvent(null)}
                            onSave={handleSavePopover}
                            onDelete={handleDeleteEvent}
                            onCancel={handleCancelEvent}
                            instructors={instructors}
                            courses={courses}
                            asesores={asesores}
                            variant="popup"
                            onToggleVariant={handleToggleEventView}
                            descriptionTemplate={descriptionTemplate}
                            onSaveTemplate={handleSaveEventTemplate}
                            classInfo={eventEnumCache[selectedEvent.id]}
                        />
                    )}
                </div>

                {/* Event Detail Panel (modo columna) */}
                {selectedEvent && eventView === 'column' && (
                    <div className="relative bg-white dark:bg-[#1e1e2e] border-l border-gray-200 dark:border-[#313244] flex flex-col overflow-hidden shrink-0" style={{ width: eventPanelWidth, height: '100%' }}>
                        {/* Resize handle */}
                        <div ref={eventPanelResizeRef}
                            className={`absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-10 flex flex-col items-center justify-center gap-0.5 group hover:bg-indigo-100 transition-colors ${isResizingEventPanel ? 'bg-indigo-200' : 'bg-gray-100 border-r border-gray-300'}`}
                            title="Arrastra para redimensionar"
                        >
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                        </div>
                        <div className="pl-2 h-full">
                            <EventPopover
                                event={selectedEvent}
                                position={popoverPos}
                                onClose={() => setSelectedEvent(null)}
                                onSave={handleSavePopover}
                                onDelete={handleDeleteEvent}
                                onCancel={handleCancelEvent}
                                instructors={instructors}
                                courses={courses}
                                asesores={asesores}
                                variant="column"
                                onToggleVariant={handleToggleEventView}
                                descriptionTemplate={descriptionTemplate}
                                onSaveTemplate={handleSaveEventTemplate}
                                classInfo={eventEnumCache[selectedEvent.id]}
                            />
                        </div>
                    </div>
                )}

                {/* Schedule Grid Panel */}
                {showGrid && (
                    <div className="bg-white border-l border-gray-200 flex flex-col overflow-hidden" style={{ width: agendaEditEvent && showInstructorTable ? Math.max(gridWidth, 620) : gridWidth, height: '100%' }}>
                        {/* Resize handle — visible gripper */}
                        <div ref={gridResizeRef}
                            className={`absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-10 flex flex-col items-center justify-center gap-0.5 group hover:bg-indigo-100 transition-colors ${isResizingGrid ? 'bg-indigo-200' : 'bg-gray-100 border-r border-gray-300'}`}
                            title="Arrastra para redimensionar"
                        >
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                            <span className="w-0.5 h-3 rounded bg-gray-400 group-hover:bg-indigo-500 transition-colors" />
                        </div>

                        {/* === AGENDA VIEW (replaces programming panel) === */}
                        {showInstructorTable ? (
                            <div className="flex flex-1 overflow-hidden">
                            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                                {/* Agenda Header */}
                                <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-2">
                                        {!instructorMode && (
                                            <button
                                                onClick={() => {
                                                    if (filterInstructorId && filterInstructorId !== gridInstructorId) {
                                                        setGridInstructorId(filterInstructorId);
                                                        setGridCourseId('');
                                                    }
                                                    setAgendaHighlightCourseId(null);
                                                    setShowInstructorTable(false);
                                                }}
                                                className="p-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition"
                                            >
                                                <FiChevronDown size={14} className="rotate-90" />
                                            </button>
                                        )}
                                        {instructorMode ? (
                                            <div className="flex items-center gap-1 text-[11px] font-bold text-indigo-700 px-1">
                                                <FiList size={12} /> Agenda
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                                                <button onClick={goToAgenda}
                                                    className={`px-2 py-1 text-[10px] rounded-md font-bold flex items-center gap-1 transition ${showInstructorTable ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                                    <FiList size={11} /> Agenda
                                                </button>
                                                <button onClick={goToProgramador}
                                                    className={`px-2 py-1 text-[10px] rounded-md font-bold flex items-center gap-1 transition ${!showInstructorTable ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                                    <FiEdit2 size={11} /> Programador
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={closePanel} className="p-1 text-gray-400 hover:text-gray-600"><FiX size={16} /></button>
                                </div>

                                {/* Selector compacto de instructor — oculto en modo instructor
                                    (queda fijo a su cuenta; no puede ver a otros instructores) */}
                                {!instructorMode && (
                                    <div className="px-3 py-2 border-b border-gray-100 bg-white shrink-0">
                                        <InstructorSearchSelect
                                            instructors={instructors}
                                            value={filterInstructorId}
                                            onChange={(id) => { setFilterInstructorId(id); setAgendaHighlightCourseId(null); setAgendaOpenCourseId(null); }}
                                            placeholder="Elegir instructor..."
                                        />
                                    </div>
                                )}

                                {/* Instructor Events Table */}
                                <div className="flex-1 overflow-y-auto flex flex-col">
                                    {filterInstructorId && (() => {
                                        const inst = instructors.find(i => i.id === filterInstructorId);
                                        const instEventsRaw = events
                                            .filter(e => (e.resource?.instructorId || e.instructorId) === filterInstructorId)
                                            .sort((a, b) => new Date(a.start) - new Date(b.start));
                                        const instEvents = showHiddenGroups
                                            ? instEventsRaw
                                            : instEventsRaw.filter(e => !isGroupInactive(
                                                e.resource?.courseId || e.courseId,
                                                e.groupNumber || e.resource?.groupNumber || 1
                                            ));
                                        const hiddenCount = instEventsRaw.length - instEvents.length;

                                        // Group by course
                                        const byCourse = {};
                                        instEvents.forEach(ev => {
                                            const cId = ev.resource?.courseId || ev.courseId || '_none';
                                            if (!byCourse[cId]) byCourse[cId] = { course: courses.find(c => c.id === cId), events: [] };
                                            byCourse[cId].events.push(ev);
                                        });

                                        return (
                                            <>
                                                {/* Mini instructor card */}
                                                <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 border-b border-gray-100 shrink-0">
                                                    {inst?.photoURL ? (
                                                        <img src={inst.photoURL} alt="" className="w-10 h-10 rounded-xl object-cover shadow-sm" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: inst?.color || '#6366f1' }}>
                                                            {(inst?.name || '?')[0]}
                                                        </div>
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="text-xs font-bold text-gray-800">{inst?.name}</p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] text-gray-500">{instEvents.length} eventos</span>
                                                            <span className="text-[10px] text-purple-600 font-medium">{instEvents.filter(e => (e.resource?.type || e.type) === 'masterclass').length} MC</span>
                                                            <span className="text-[10px] text-blue-600 font-medium">{instEvents.filter(e => (e.resource?.type || e.type) !== 'masterclass').length} CL</span>
                                                        </div>
                                                    </div>
                                                    {(() => {
                                                        const cancel = instEventsRaw.filter(e => e.status === 'cancelled')
                                                            .sort((a, b) => new Date(a.start) - new Date(b.start));
                                                        if (cancel.length === 0) return null;
                                                        return (
                                                            <div className="ml-auto shrink-0 relative">
                                                                <button onClick={() => setShowCancelledList(s => !s)}
                                                                    className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-bold hover:bg-amber-200 transition">
                                                                    <FiAlertTriangle size={11} /> {cancel.length} por reprogramar
                                                                </button>
                                                                {showCancelledList && (
                                                                    <>
                                                                        <div className="fixed inset-0 z-[55]" onClick={() => setShowCancelledList(false)} />
                                                                        <div className="absolute right-0 mt-1 z-[56] w-64 max-h-72 overflow-y-auto bg-white border border-amber-200 rounded-xl shadow-lg p-1">
                                                                            {cancel.map(ev => {
                                                                                const cName = courses.find(c => c.id === (ev.resource?.courseId || ev.courseId))?.name || ev.title || 'Clase';
                                                                                return (
                                                                                    <div key={ev.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-amber-50 rounded-lg">
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-[10px] font-bold text-gray-700">{new Date(ev.start).toLocaleDateString('es', { day: '2-digit', month: 'short' })}</p>
                                                                                            <p className="text-[9px] text-gray-400 truncate">{cName}</p>
                                                                                        </div>
                                                                                        <button onClick={() => { openReprogram(ev); setShowCancelledList(false); }}
                                                                                            className="px-2 py-1 bg-amber-600 text-white rounded-md text-[9px] font-bold flex items-center gap-1 hover:bg-amber-700 shrink-0">
                                                                                            <FiRefreshCw size={9} /> Reprogramar
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {/* View toggle: Fecha / Curso */}
                                                <div className="px-3 py-2 border-b border-gray-100 bg-white sticky top-0 z-10 shrink-0 flex items-center gap-1">
                                                    <button onClick={() => setAgendaViewMode?.('fecha')}
                                                        className={`px-2.5 py-1 text-[10px] rounded-md font-medium transition ${(!agendaViewMode || agendaViewMode === 'fecha') ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                                                        Por Fecha
                                                    </button>
                                                    <button onClick={() => setAgendaViewMode?.('curso')}
                                                        className={`px-2.5 py-1 text-[10px] rounded-md font-medium transition ${agendaViewMode === 'curso' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                                                        Por Curso
                                                    </button>
                                                    <div className="flex-1" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAgendaExpand(true)}
                                                        disabled={instEvents.length === 0}
                                                        className="px-2 py-1 text-[10px] rounded-md font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1"
                                                        title="Expandir agenda"
                                                    >
                                                        <FiMaximize2 size={11} /> Expandir
                                                    </button>
                                                    <div className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowExcelChooser(s => !s)}
                                                            disabled={instEvents.length === 0}
                                                            className="px-2 py-1 text-[10px] rounded-md font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1"
                                                            title="Exportar a Excel"
                                                        >
                                                            <FiDownload size={11} /> Excel
                                                        </button>
                                                        {showExcelChooser && (() => {
                                                            const byCG = {};
                                                            instEventsRaw.forEach(ev => {
                                                                const cId = ev.resource?.courseId || ev.courseId || '_none';
                                                                const gn = ev.groupNumber || ev.resource?.groupNumber || 1;
                                                                const key = `${cId}__${gn}`;
                                                                if (!byCG[key]) byCG[key] = { cId, gn, name: courses.find(c => c.id === cId)?.name || ev.title || 'Sin curso', events: [] };
                                                                byCG[key].events.push(ev);
                                                            });
                                                            const todayStr = new Date().toISOString().slice(0, 10);
                                                            const entries = Object.values(byCG).sort((a, b) => (a.name + a.gn).localeCompare(b.name + b.gn));
                                                            return (
                                                                <>
                                                                    <div className="fixed inset-0 z-[55]" onClick={() => setShowExcelChooser(false)} />
                                                                    <div className="absolute right-0 mt-1 z-[56] w-60 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg p-1">
                                                                        <button onClick={() => { exportAgendaToXlsx({ instructor: inst, courses, events: instEventsRaw, toast, isGroupInactive: showHiddenGroups ? undefined : isGroupInactive }); setShowExcelChooser(false); }}
                                                                            className="w-full text-left px-2 py-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 rounded-lg">
                                                                            ⬇ Exportar todo
                                                                        </button>
                                                                        <div className="border-t border-gray-100 my-1" />
                                                                        {entries.map(en => (
                                                                            <button key={`${en.cId}-${en.gn}`}
                                                                                onClick={() => { exportAgendaToXlsx({ instructor: inst, courses, events: en.events, toast, fileName: `${String(en.name).replace(/[^\w\d]+/g, '_')}_G${en.gn}_${todayStr}.xlsx` }); setShowExcelChooser(false); }}
                                                                                className="w-full text-left px-2 py-1.5 text-[10px] text-gray-700 hover:bg-gray-50 rounded-lg truncate">
                                                                                {en.name} · <span className="font-bold text-indigo-600">G{en.gn}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                    <span className="text-[9px] text-gray-400 ml-1">{instEvents.length} total</span>
                                                </div>

                                                {hiddenCount > 0 && (
                                                    <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[10px]">
                                                        <FiEyeOff size={11} className="text-amber-600 shrink-0" />
                                                        <span className="text-amber-700 font-medium flex-1">
                                                            {hiddenCount} evento{hiddenCount !== 1 ? 's' : ''} oculto{hiddenCount !== 1 ? 's' : ''} (grupos terminados)
                                                        </span>
                                                        <button
                                                            onClick={() => setShowHiddenGroups(s => !s)}
                                                            className="px-2 py-0.5 bg-amber-200 hover:bg-amber-300 text-amber-800 rounded text-[10px] font-bold"
                                                        >
                                                            {showHiddenGroups ? 'Volver a ocultar' : 'Mostrar todos'}
                                                        </button>
                                                    </div>
                                                )}

                                                {instEvents.length === 0 ? (
                                                    <div className="text-center py-12 text-gray-400">
                                                        <FiCalendar size={28} className="mx-auto mb-2 opacity-30" />
                                                        <p className="text-xs">Sin eventos</p>
                                                    </div>
                                                ) : agendaViewMode === 'curso' ? (
                                                    /* === BY COURSE VIEW (cajas + drill-down) === */
                                                    (() => {
                                                        const byCourseRaw = {};
                                                        instEventsRaw.forEach(ev => {
                                                            const cId = ev.resource?.courseId || ev.courseId || '_none';
                                                            if (!byCourseRaw[cId]) byCourseRaw[cId] = { course: courses.find(c => c.id === cId), events: [] };
                                                            byCourseRaw[cId].events.push(ev);
                                                        });
                                                        const courseIds = Object.keys(byCourseRaw);

                                                        // ---- DETALLE de un curso ----
                                                        if (agendaOpenCourseId && byCourseRaw[agendaOpenCourseId]) {
                                                            const cId = agendaOpenCourseId;
                                                            const group = byCourseRaw[cId];
                                                            const agCourse = group.course;
                                                            const agMcPerGrp = agCourse?.totalMasterclasses || agCourse?.masterclassesPerGroup || 0;
                                                            const agClPerGrp = agCourse?.totalSessions || agCourse?.classesPerGroup || 0;
                                                            const byGrpNum = {};
                                                            group.events.forEach(ev => {
                                                                const gn = ev.groupNumber || ev.resource?.groupNumber || 1;
                                                                if (!byGrpNum[gn]) byGrpNum[gn] = [];
                                                                byGrpNum[gn].push(ev);
                                                            });
                                                            const groupNums = Object.keys(byGrpNum).sort((a, b) => Number(a) - Number(b));
                                                            return (
                                                                <div className="flex-1 overflow-y-auto">
                                                                    <div className="sticky top-0 z-[6] px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                                                                        <button onClick={() => { setAgendaOpenCourseId(null); setAgendaHighlightCourseId(null); }}
                                                                            className="p-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition" title="Volver a cursos">
                                                                            <FiChevronDown size={14} className="rotate-90" />
                                                                        </button>
                                                                        <span className="flex-1 truncate text-xs font-bold text-gray-800">{agCourse?.name || 'Sin curso'}</span>
                                                                        {(() => {
                                                                            const courseCancel = group.events.filter(e => e.status === 'cancelled')
                                                                                .sort((a, b) => new Date(a.start) - new Date(b.start));
                                                                            if (courseCancel.length === 0) return null;
                                                                            return (
                                                                                <div className="relative shrink-0">
                                                                                    <button onClick={() => setShowCourseCancelledList(s => !s)}
                                                                                        className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[9px] font-bold hover:bg-amber-200 transition">
                                                                                        <FiAlertTriangle size={10} /> {courseCancel.length} reprog.
                                                                                    </button>
                                                                                    {showCourseCancelledList && (
                                                                                        <>
                                                                                            <div className="fixed inset-0 z-[55]" onClick={() => setShowCourseCancelledList(false)} />
                                                                                            <div className="absolute right-0 mt-1 z-[56] w-56 max-h-72 overflow-y-auto bg-white border border-amber-200 rounded-xl shadow-lg p-1">
                                                                                                {courseCancel.map(ev => (
                                                                                                    <div key={ev.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-amber-50 rounded-lg">
                                                                                                        <p className="flex-1 text-[10px] font-bold text-gray-700">{new Date(ev.start).toLocaleDateString('es', { day: '2-digit', month: 'short' })}</p>
                                                                                                        <button onClick={() => { openReprogram(ev); setShowCourseCancelledList(false); }}
                                                                                                            className="px-2 py-1 bg-amber-600 text-white rounded-md text-[9px] font-bold flex items-center gap-1 hover:bg-amber-700 shrink-0">
                                                                                                            <FiRefreshCw size={9} /> Reprogramar
                                                                                                        </button>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                        <span className="text-[9px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">{group.events.filter(e => (e.resource?.type || e.type) === 'masterclass').length} MC</span>
                                                                        <span className="text-[9px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{group.events.filter(e => (e.resource?.type || e.type) !== 'masterclass').length} CL</span>
                                                                    </div>
                                                                    {groupNums.map(gn => {
                                                                        const grpEvents = byGrpNum[gn].sort((a, b) => new Date(a.start) - new Date(b.start));
                                                                        const inactive = isGroupInactive(cId, gn);
                                                                        // El ojo colapsa Y termina el grupo: colapsado === inactivo (siempre, sin depender de "Mostrar todos")
                                                                        const collapsed = inactive;
                                                                        let mcCount = 0, clCount = 0;
                                                                        const cleanName = String(agCourse?.name || 'Curso').replace(/[^\w\d]+/g, '_');
                                                                        const todayStr = new Date().toISOString().slice(0, 10);
                                                                        return (
                                                                            <div key={gn} ref={el => { if (el) groupRefs.current[`${cId}-${gn}`] = el; }}>
                                                                                <div className={`px-3 py-1.5 border-b flex items-center gap-2 ${inactive ? 'bg-gray-100 border-gray-200' : 'bg-indigo-50/50 border-indigo-100'} ${collapsed ? 'cursor-pointer hover:bg-gray-200/60' : ''}`}
                                                                                    onClick={collapsed ? () => toggleGroupInactive(cId, gn) : undefined}>
                                                                                    <span className={`text-[10px] font-bold ${inactive ? 'text-gray-500' : 'text-indigo-600'}`}>Grupo {gn}</span>
                                                                                    <span className="text-[8px] text-gray-400">{grpEvents.length} eventos{inactive ? ' · oculto' : ''}</span>
                                                                                    <div className="ml-auto flex items-center gap-0.5" data-html2canvas-ignore="true">
                                                                                        <button onClick={(e) => { e.stopPropagation(); exportAgendaToXlsx({ instructor: inst, courses, events: grpEvents, toast, fileName: `${cleanName}_G${gn}_${todayStr}.xlsx` }); }}
                                                                                            className="p-0.5 rounded text-emerald-600 hover:bg-emerald-50" title="Exportar grupo a Excel">
                                                                                            <FiDownload size={11} />
                                                                                        </button>
                                                                                        <button onClick={(e) => { e.stopPropagation(); exportAgendaToImage(groupRefs.current[`${cId}-${gn}`], `${cleanName}_G${gn}_${todayStr}.png`, toast); }}
                                                                                            className="p-0.5 rounded text-indigo-600 hover:bg-indigo-50" title="Exportar grupo a imagen">
                                                                                            <FiImage size={11} />
                                                                                        </button>
                                                                                        <button onClick={(e) => { e.stopPropagation(); toggleGroupInactive(cId, gn); }}
                                                                                            className={`p-0.5 rounded hover:bg-white/60 ${inactive ? 'text-gray-500' : 'text-indigo-600'}`}
                                                                                            title={inactive ? 'Reactivar / mostrar grupo' : 'Colapsar grupo'}>
                                                                                            {inactive ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                {!collapsed && (
                                                                                    <table className="w-full text-[13px]">
                                                                                        <tbody>
                                                                                            {grpEvents.map((ev, idx) => {
                                                                                                const isMC = (ev.resource?.type || ev.type) === 'masterclass';
                                                                                                const isCancelled = ev.status === 'cancelled';
                                                                                                if (!isCancelled) { if (isMC) mcCount++; else clCount++; }
                                                                                                const num = isCancelled ? '✕' : (isMC
                                                                                                    ? (agMcPerGrp > 0 ? ((mcCount - 1) % agMcPerGrp) + 1 : mcCount)
                                                                                                    : (agClPerGrp > 0 ? ((clCount - 1) % agClPerGrp) + 1 : clCount));
                                                                                                const startD = new Date(ev.start);
                                                                                                const endD = new Date(ev.end);
                                                                                                const isPast = !isCancelled && endD < new Date();
                                                                                                const rowTone = isCancelled
                                                                                                    ? 'bg-red-100/60 line-through'
                                                                                                    : isPast
                                                                                                        ? 'bg-gray-100/80'
                                                                                                        : 'bg-emerald-50/60';
                                                                                                return (
                                                                                                    <tr key={ev.id || idx} className={`border-t border-gray-100 ${rowTone} hover:bg-indigo-50/40 transition-colors group/row`}>
                                                                                                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                                                                                                            <div className="leading-tight">
                                                                                                                <div>{startD.toLocaleDateString('es', { day: '2-digit', month: 'short' })}</div>
                                                                                                                <div className="text-[9px] text-gray-400 capitalize">{startD.toLocaleDateString('es', { weekday: 'long' })}</div>
                                                                                                            </div>
                                                                                                        </td>
                                                                                                        <td className="px-2 py-2 font-mono text-gray-500 whitespace-nowrap">
                                                                                                            {startD.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true })}-{endD.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                                                                        </td>
                                                                                                        <td className="px-2 py-2">
                                                                                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isCancelled ? 'bg-red-100 text-red-500' : isMC ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                                                                {isMC ? `MC ${num}` : `CL ${num}`}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        {!instructorMode && (
                                                                                                        <td className="px-1 py-2">
                                                                                                            <select
                                                                                                                value={ev.groupNumber || ev.resource?.groupNumber || 1}
                                                                                                                onChange={async (e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    const newGrp = Number(e.target.value);
                                                                                                                    try {
                                                                                                                        await updateSchedule(ev.id, { groupNumber: newGrp });
                                                                                                                        setEvents(prev => prev.map(x => x.id === ev.id ? { ...x, groupNumber: newGrp } : x));
                                                                                                                        toast.success(`Movido a Grupo ${newGrp}`);
                                                                                                                    } catch { toast.error('Error'); }
                                                                                                                }}
                                                                                                                className="opacity-0 group-hover/row:opacity-100 text-[8px] px-1 py-0.5 border border-gray-200 rounded bg-white w-12 cursor-pointer transition-opacity"
                                                                                                                title="Cambiar grupo"
                                                                                                            >
                                                                                                                {[1,2,3,4,5].map(n => <option key={n} value={n}>G{n}</option>)}
                                                                                                            </select>
                                                                                                        </td>
                                                                                                        )}
                                                                                                        {!instructorMode && (
                                                                                                        <td className="px-1 py-2">
                                                                                                            <div className="flex items-center gap-0.5">
                                                                                                                {isCancelled && (
                                                                                                                    <button onClick={(e) => { e.stopPropagation(); openReprogram(ev); }}
                                                                                                                        className="p-1 rounded text-amber-600 hover:bg-amber-50"
                                                                                                                        title="Reprogramar (elegir nueva fecha)">
                                                                                                                        <FiRefreshCw size={11} />
                                                                                                                    </button>
                                                                                                                )}
                                                                                                                <button
                                                                                                                    onClick={(e) => { e.stopPropagation(); setAgendaEditEvent(ev); }}
                                                                                                                    className={`p-1 rounded transition-all ${agendaEditEvent?.id === ev.id ? 'text-indigo-600 bg-indigo-100' : 'opacity-0 group-hover/row:opacity-100 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                                                                                                    title="Editar"
                                                                                                                >
                                                                                                                    <FiEdit2 size={11} />
                                                                                                                </button>
                                                                                                            </div>
                                                                                                        </td>
                                                                                                        )}
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        }

                                                        // ---- REJILLA de cajas ----
                                                        return (
                                                            <div className="flex-1 overflow-y-auto p-3">
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    {courseIds.map(cId => {
                                                                        const group = byCourseRaw[cId];
                                                                        const groups = [...new Set(group.events.map(e => e.groupNumber || e.resource?.groupNumber || 1))];
                                                                        const hiddenN = groups.filter(gn => isGroupInactive(cId, gn)).length;
                                                                        const mc = group.events.filter(e => (e.resource?.type || e.type) === 'masterclass').length;
                                                                        const cl = group.events.length - mc;
                                                                        const courseCancel = group.events.filter(e => e.status === 'cancelled').length;
                                                                        return (
                                                                            <button key={cId} type="button"
                                                                                onClick={() => { setAgendaOpenCourseId(cId); setAgendaHighlightCourseId(cId); }}
                                                                                className="rounded-xl border border-gray-200 p-3 text-left hover:border-indigo-300 hover:shadow-sm transition bg-white flex flex-col gap-2">
                                                                                <span className="text-[11px] font-bold text-gray-800 line-clamp-2 leading-tight">{group.course?.name || 'Sin curso'}</span>
                                                                                <div className="flex items-center gap-1 flex-wrap">
                                                                                    <span className="text-[8px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-bold">{groups.length} grupo{groups.length !== 1 ? 's' : ''}</span>
                                                                                    <span className="text-[8px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full font-medium">{mc} MC</span>
                                                                                    <span className="text-[8px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full font-medium">{cl} CL</span>
                                                                                    {courseCancel > 0 && <span className="text-[8px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><FiAlertTriangle size={8} />{courseCancel} reprog.</span>}
                                                                                    {hiddenN > 0 && <span className="text-[8px] text-gray-400 flex items-center gap-0.5"><FiEyeOff size={8} />{hiddenN}</span>}
                                                                                </div>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()
                                                ) : (
                                                    /* === BY DATE VIEW (original) === */
                                                    <div className="flex-1 overflow-y-auto">
                                                        <table className="w-full text-[11px]">
                                                            <thead className="sticky top-0 bg-gray-100 z-[5]">
                                                                <tr className="text-gray-500 font-bold uppercase text-[9px]">
                                                                    <th className="px-3 py-2 text-left">Fecha</th>
                                                                    <th className="px-2 py-2 text-left">Curso</th>
                                                                    <th className="px-2 py-2 text-left">Hora</th>
                                                                    <th className="px-2 py-2 w-10"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {instEvents.map((ev, idx) => {
                                                                    const courseName = courses.find(c => c.id === (ev.resource?.courseId || ev.courseId))?.name || ev.title || '-';
                                                                    const startD = new Date(ev.start);
                                                                    const endD = new Date(ev.end);
                                                                    const isCancelled = ev.status === 'cancelled';
                                                                    const isMC = (ev.resource?.type || ev.type) === 'masterclass';
                                                                    return (
                                                                        <tr key={ev.id || idx} className={`border-t border-gray-100 ${isCancelled ? 'opacity-60' : ''} ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-indigo-50/30 transition-colors group`}>
                                                                            <td className={`px-3 py-2 font-medium text-gray-800 whitespace-nowrap ${isCancelled ? 'line-through' : ''}`}>
                                                                                <div className="leading-tight">
                                                                                    <div>{startD.toLocaleDateString('es', { day: '2-digit', month: 'short' })}</div>
                                                                                    <div className="text-[9px] text-gray-400 capitalize">{startD.toLocaleDateString('es', { weekday: 'long' })}</div>
                                                                                </div>
                                                                            </td>
                                                                            <td className={`px-2 py-2 text-gray-600 truncate max-w-[120px] ${isCancelled ? 'line-through' : ''}`} title={courseName}>{courseName}</td>
                                                                            <td className={`px-2 py-2 font-mono text-gray-500 whitespace-nowrap ${isCancelled ? 'line-through' : ''}`}>
                                                                                {startD.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true })}-{endD.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                                            </td>
                                                                            <td className="px-2 py-2">
                                                                                {isCancelled ? (
                                                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-600">Cancelada</span>
                                                                                ) : (
                                                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isMC ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                                        {isMC ? 'MC' : 'CL'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-2 py-2">
                                                                                {isCancelled ? (
                                                                                    <button onClick={(e) => { e.stopPropagation(); openReprogram(ev); }}
                                                                                        className="p-1 rounded text-amber-600 hover:bg-amber-50"
                                                                                        title="Reprogramar (elegir nueva fecha)">
                                                                                        <FiRefreshCw size={11} />
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); setAgendaEditEvent(ev); }}
                                                                                        className={`p-1 rounded transition-all ${agendaEditEvent?.id === ev.id ? 'text-indigo-600 bg-indigo-100' : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                                                                        title="Editar evento"
                                                                                    >
                                                                                        <FiEdit2 size={11} />
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Back button footer */}
                                <div className="px-3 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
                                    <button
                                        onClick={() => {
                                            if (filterInstructorId && filterInstructorId !== gridInstructorId) {
                                                setGridInstructorId(filterInstructorId);
                                                setGridCourseId('');
                                            }
                                            setAgendaHighlightCourseId(null);
                                            setShowInstructorTable(false);
                                        }}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-colors">
                                        <FiChevronDown size={14} className="rotate-90" /> Volver a Programar
                                    </button>
                                </div>
                            </div>

                            {/* === INLINE EDIT COLUMN (replaces popup) === */}
                            {agendaEditEvent && (() => {
                                const ev = agendaEditEvent;
                                const evIsCancelled = ev.status === 'cancelled';
                                const evInstructor = instructors.find(i => i.id === ev.instructorId || i.id === ev.resource?.instructorId);
                                const evCourse = courses.find(c => c.id === ev.courseId || c.id === ev.resource?.courseId);
                                const evStart = ev.start instanceof Date ? ev.start : new Date(ev.start);
                                const evEnd = ev.end instanceof Date ? ev.end : new Date(ev.end);
                                const evDateStr = toLocalDateStr(evStart);
                                const evStartTime = `${String(evStart.getHours()).padStart(2,'0')}:${String(evStart.getMinutes()).padStart(2,'0')}`;
                                const evEndTime = `${String(evEnd.getHours()).padStart(2,'0')}:${String(evEnd.getMinutes()).padStart(2,'0')}`;
                                const isMC = (ev.resource?.type || ev.type) === 'masterclass';
                                const timeSlots = [];
                                for (let h = 5; h <= 23; h++) {
                                    [0, 30].forEach(m => {
                                        const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                        const ampm = h >= 12 ? 'pm' : 'am';
                                        const h12 = h % 12 || 12;
                                        timeSlots.push({ val, label: `${h12}:${String(m).padStart(2, '0')} ${ampm}` });
                                    });
                                }

                                return (
                                    <div key={ev.id} className="w-64 border-l border-gray-200 bg-white flex flex-col shrink-0 animate-fadeIn">
                                        {/* Edit Header */}
                                        <div className="px-3 py-2.5 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50 flex items-center justify-between shrink-0">
                                            <div className="flex items-center gap-2">
                                                <FiEdit2 size={12} className="text-indigo-600" />
                                                <span className="text-xs font-bold text-gray-800">Editar Evento</span>
                                            </div>
                                            <button onClick={() => setAgendaEditEvent(null)} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
                                                <FiX size={14} />
                                            </button>
                                        </div>

                                        {/* Edit Form - scrollable */}
                                        <form className="flex-1 overflow-y-auto p-3 space-y-3" onSubmit={async (e) => {
                                            e.preventDefault();
                                            const fd = new FormData(e.target);
                                            const dateVal = fd.get('eventDate');
                                            const startVal = fd.get('startTime');
                                            const endVal = fd.get('endTime');
                                            const updates = {
                                                title: fd.get('title'),
                                                notes: fd.get('description'),
                                                claseLiveUrl: fd.get('claseLiveUrl'),
                                                claseMeetingId: fd.get('claseMeetingId'),
                                                clasePassword: fd.get('clasePassword'),
                                            };
                                            if (dateVal && startVal) {
                                                const [h, m] = startVal.split(':').map(Number);
                                                const newStart = new Date(dateVal + 'T00:00:00');
                                                newStart.setHours(h, m, 0, 0);
                                                updates.start = newStart;
                                            }
                                            if (dateVal && endVal) {
                                                const [h, m] = endVal.split(':').map(Number);
                                                const newEnd = new Date(dateVal + 'T00:00:00');
                                                newEnd.setHours(h, m, 0, 0);
                                                updates.end = newEnd;
                                            }
                                            await handleSavePopover(ev.id, updates);
                                            setAgendaEditEvent(null);
                                        }}>
                                            {/* Cancelled banner */}
                                            {evIsCancelled && (
                                                <div className="px-2.5 py-2 bg-red-50 border border-red-200 rounded-lg space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-red-600 text-[10px] font-bold">CANCELADA</span>
                                                        <span className="text-red-400 text-[9px] flex-1">Sesión cancelada</span>
                                                        <button type="button" onClick={() => { handleCancelEvent(ev.id, true); setAgendaEditEvent(null); }}
                                                            className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold hover:bg-green-200 transition">
                                                            Reactivar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Type badge + Course */}
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${evIsCancelled ? 'bg-red-100 text-red-500' : isMC ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {isMC ? 'Masterclass' : 'Clase'}
                                                </span>
                                                {evCourse && <span className="text-[10px] text-gray-500 truncate flex-1">{evCourse.name}</span>}
                                            </div>

                                            {/* Title */}
                                            <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">Título</label>
                                                <input name="title" defaultValue={ev.title}
                                                    className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none bg-gray-50 font-medium" />
                                            </div>

                                            {/* Date */}
                                            <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">Fecha</label>
                                                <input name="eventDate" type="date" defaultValue={evDateStr}
                                                    className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none bg-gray-50 cursor-pointer" />
                                            </div>

                                            {/* Time row */}
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">Inicio</label>
                                                    <select name="startTime" defaultValue={evStartTime}
                                                        className="w-full text-xs px-1.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-indigo-400 outline-none cursor-pointer">
                                                        {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">Fin</label>
                                                    <select name="endTime" defaultValue={evEndTime}
                                                        className="w-full text-xs px-1.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-indigo-400 outline-none cursor-pointer">
                                                        {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Instructor (read-only) */}
                                            {evInstructor && (
                                                <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
                                                    {evInstructor.photoURL ? (
                                                        <img src={evInstructor.photoURL} alt="" className="w-5 h-5 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white" style={{ backgroundColor: evInstructor.color || '#6366f1' }}>
                                                            {(evInstructor.name || '?')[0]}
                                                        </div>
                                                    )}
                                                    <p className="text-[10px] font-medium text-gray-700 truncate">{evInstructor.name}</p>
                                                </div>
                                            )}

                                            {/* Enlace clase en vivo (solo para Clase) */}
                                            {!isMC && (
                                                <div className="space-y-1.5">
                                                    <label className="text-[10px] font-semibold text-blue-600 uppercase flex items-center gap-1">
                                                        <FiExternalLink size={10} /> Enlace clase en vivo
                                                    </label>
                                                    <input name="claseLiveUrl" type="url"
                                                        defaultValue={ev.claseLiveUrl || evCourse?.claseVariables?.claseLiveUrl || ''}
                                                        placeholder="https://zoom.us/j/..."
                                                        className="w-full text-[10px] px-2 py-1.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none bg-blue-50/30" />
                                                    <div className="grid grid-cols-2 gap-1.5">
                                                        <input name="claseMeetingId" type="text"
                                                            defaultValue={ev.claseMeetingId || evCourse?.claseVariables?.claseMeetingId || ''}
                                                            placeholder="ID reunión"
                                                            className="text-[10px] px-2 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-blue-50/30" />
                                                        <input name="clasePassword" type="text"
                                                            defaultValue={ev.clasePassword || evCourse?.claseVariables?.clasePassword || ''}
                                                            placeholder="Contraseña"
                                                            className="text-[10px] px-2 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-blue-50/30" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* MC: prefiere link asignado por distribución, fallback al default del curso */}
                                            {isMC && (() => {
                                                const mcUrl = ev.mcLiveUrl || evCourse?.defaultVariables?.zoomLink || '';
                                                const mcId = ev.mcMeetingId || evCourse?.defaultVariables?.meetingId || '';
                                                const mcPass = ev.mcPassword || evCourse?.defaultVariables?.password || '';
                                                if (!mcUrl) return null;
                                                return (
                                                    <div className="bg-blue-50 rounded-lg p-2 space-y-1">
                                                        <label className="text-[10px] font-semibold text-blue-600 uppercase">Enlace MC asignado</label>
                                                        <a href={mcUrl} target="_blank" rel="noopener noreferrer"
                                                            className="text-[9px] text-blue-600 hover:underline flex items-center gap-1 truncate">
                                                            <FiExternalLink size={9} /> {mcUrl.slice(0, 35)}...
                                                        </a>
                                                        {mcId && (
                                                            <p className="text-[9px] text-gray-500">ID: {mcId} | Pass: {mcPass || '-'}</p>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* Description */}
                                            <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase mb-1 block">Notas</label>
                                                <textarea name="description" defaultValue={ev.notes || ev.description || ''}
                                                    placeholder="Notas adicionales..."
                                                    rows={2}
                                                    className="w-full text-[10px] px-2.5 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none resize-none bg-gray-50" />
                                            </div>

                                            {/* Action buttons */}
                                            <div className="flex gap-2 pt-1">
                                                <button type="button" onClick={() => setAgendaEditEvent(null)}
                                                    className="flex-1 text-[11px] py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium transition">
                                                    Cerrar
                                                </button>
                                                <button type="submit"
                                                    className="flex-1 text-[11px] py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-bold transition flex items-center justify-center gap-1 shadow-sm">
                                                    <FiCheck size={11} /> Guardar
                                                </button>
                                            </div>

                                            {/* Cancel / Delete actions */}
                                            <div className="border-t border-gray-100 pt-2 space-y-1.5">
                                                {!evIsCancelled && (
                                                    <button type="button" onClick={() => { handleCancelEvent(ev.id); setAgendaEditEvent(null); }}
                                                        className="w-full text-[10px] py-1.5 text-orange-600 hover:bg-orange-50 border border-orange-200 rounded-lg transition flex items-center justify-center gap-1 font-medium">
                                                        <FiAlertTriangle size={10} /> Cancelar clase
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => { handleDeleteEvent(ev.id); setAgendaEditEvent(null); }}
                                                    className="w-full text-[10px] py-1.5 text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition flex items-center justify-center gap-1 font-medium">
                                                    <FiTrash2 size={10} /> Eliminar evento
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                );
                            })()}
                            </div>
                        ) : (
                            <>
                                {/* === PROGRAMMING VIEW (original) === */}
                                {/* Compact Header */}
                                <div className="px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                                            <button onClick={goToAgenda}
                                                className={`px-2 py-1 text-[10px] rounded-md font-bold flex items-center gap-1 transition ${showInstructorTable ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                                <FiList size={11} /> Agenda
                                            </button>
                                            {!instructorMode && (
                                                <button onClick={goToProgramador}
                                                    className={`px-2 py-1 text-[10px] rounded-md font-bold flex items-center gap-1 transition ${!showInstructorTable ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                                                    <FiEdit2 size={11} /> Programador
                                                </button>
                                            )}
                                        </div>
                                        {hasDraft && <span className="text-[8px] bg-amber-400 text-white px-1.5 py-px rounded-full font-bold">{gridRows.filter(r => r.date).length}</span>}
                                        {savedDrafts.length > 0 && <span className="text-[8px] bg-blue-500 text-white px-1.5 py-px rounded-full font-bold">{savedDrafts.length}b</span>}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        {savedDrafts.length > 0 && (
                                            <button onClick={() => setShowDraftsList(!showDraftsList)} className={`p-1 rounded ${showDraftsList ? 'text-blue-700 bg-blue-100' : 'text-blue-500'}`} title="Borradores"><FiBookOpen size={12} /></button>
                                        )}
                                        {hasDraft && (
                                            <button onClick={clearDraft} className="p-1 text-amber-500 rounded" title="Limpiar"><FiTrash2 size={12} /></button>
                                        )}
                                        <button onClick={closePanel} className="p-1 text-gray-400 hover:text-gray-600 rounded"><FiX size={14} /></button>
                                    </div>
                                </div>

                                {/* Saved Drafts List */}
                                {showDraftsList && savedDrafts.length > 0 && (() => {
                                    const relativeTime = (savedAt) => {
                                        if (!savedAt) return '';
                                        const ts = savedAt?.toDate ? savedAt.toDate() : new Date(savedAt);
                                        const diff = Date.now() - ts.getTime();
                                        if (diff < 60000) return 'hace un momento';
                                        if (diff < 3600000) return `hace ${Math.floor(diff / 60000)}m`;
                                        if (diff < 86400000) return `hace ${Math.floor(diff / 3600000)}h`;
                                        return ts.toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                                    };
                                    return (
                                        <div className="px-3 py-2 border-b border-gray-200 bg-blue-50/50 max-h-52 overflow-y-auto shrink-0">
                                            <p className="text-[10px] font-bold text-blue-700 uppercase mb-2 flex items-center gap-1">
                                                ☁️ Borradores en la nube ({savedDrafts.length}/40)
                                            </p>
                                            <div className="space-y-2">
                                                {savedDrafts.map(draft => {
                                                    const isOwner = user?.uid && (draft.lastEditedBy?.uid === user.uid || draft.createdBy?.uid === user.uid);
                                                    const editor = draft.lastEditedBy || draft.createdBy;
                                                    return (
                                                        <div key={draft.id} className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden">
                                                            {/* Creator row */}
                                                            <div className="flex items-center gap-2 px-2.5 pt-2 pb-1">
                                                                {editor?.photoURL ? (
                                                                    <img src={editor.photoURL} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                                                                ) : (
                                                                    <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center text-[8px] font-bold text-white shrink-0">
                                                                        {(editor?.displayName || editor?.email || '?')[0].toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <span className="text-[10px] font-semibold text-gray-700 truncate flex-1">
                                                                    {editor?.displayName || editor?.email || 'Colaborador'}
                                                                </span>
                                                                <span className="text-[9px] text-gray-400 shrink-0">{relativeTime(draft.updatedAt || draft.savedAt)}</span>
                                                            </div>
                                                            {/* Draft info + actions */}
                                                            <div className="flex items-center justify-between px-2.5 pb-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[11px] font-semibold text-gray-800 truncate">{draft.courseName || 'Sin curso'}</p>
                                                                    <p className="text-[10px] text-gray-500 truncate">{draft.instructorName} · {draft.rowCount} fecha{draft.rowCount !== 1 ? 's' : ''}</p>
                                                                </div>
                                                                <div className="flex items-center gap-1 ml-2 shrink-0">
                                                                    <button onClick={() => loadDraftFromList(draft)} className="px-2 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-bold hover:bg-blue-700 transition">Cargar</button>
                                                                    {isOwner && (
                                                                        <button onClick={() => deleteDraftFromList(draft)} className="p-1 text-red-400 hover:text-red-600 transition" title="Solo tú puedes eliminar tu borrador">
                                                                            <FiTrash2 size={12} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Curso + Instructor in one row */}
                                <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50/50 shrink-0">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[9px] font-bold text-gray-400 uppercase">Curso</label>
                                            <div className="mt-0.5"><HxSelect
                                                size="sm"
                                                className="full"
                                                minWidth={0}
                                                value={gridCourseId}
                                                onChange={setGridCourseId}
                                                options={courseOpts((() => {
                                                    const filtered = gridInstructorId
                                                        ? courses.filter(c => c.instructorId === gridInstructorId || c.instructor === gridInstructorId)
                                                        : courses;
                                                    return gridInstructorId && filtered.length === 0 ? courses : filtered;
                                                })(), 'Sin curso específico')}
                                                placeholder="Sin curso específico"
                                                searchPlaceholder="Buscar curso…"
                                                icon={LBook}
                                                ariaLabel="Curso"
                                            /></div>
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-bold text-gray-400 uppercase">Instructor</label>
                                            <InstructorSearchSelect
                                                instructors={instructors}
                                                value={gridInstructorId}
                                                onChange={(id) => { setGridInstructorId(id); setGridCourseId(''); }}
                                                placeholder="Elegir..."
                                                className="mt-0.5"
                                            />
                                        </div>
                                    </div>
                                    {gridInstructorId && (
                                        <InstructorAvailabilityCard
                                            instructor={instructors.find(i => i.id === gridInstructorId)}
                                        />
                                    )}
                                    {gridInstructorId && (() => {
                                        const inst = instructors.find(i => i.id === gridInstructorId);
                                        if (!inst) return null;
                                        const availDays = Object.entries(inst.availability || {}).filter(([, v]) => v && v.length > 0).map(([k]) => DAY_NAMES_ES[k]?.slice(0, 3) || k);
                                        return (
                                            <div className="flex items-center justify-between mt-1.5">
                                                <div className="flex items-center gap-1 text-[9px] text-gray-500">
                                                    <FiClock size={8} /> {availDays.join(', ') || 'Sin config.'}
                                                    {inst.diasBloqueados?.length > 0 && <span className="text-amber-600">({inst.diasBloqueados.length} bloq.)</span>}
                                                </div>
                                                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/horario/${gridInstructorId}`); toast.success('Enlace copiado', { icon: '🔗' }); }}
                                                    className="text-[9px] text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-0.5">
                                                    <FiExternalLink size={8} /> Link
                                                </button>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Barra compacta de acciones (reemplaza el selector de 3 opciones) */}
                                {gridCourseId && gridInstructorId && existingCourseEvents.length > 0 && (() => {
                                    const maxG = existingGroups.length > 0 ? Math.max(...existingGroups) : 1;
                                    const actionBtn = (mode, label, activeCls, disabled = false) => (
                                        <button
                                            onClick={() => { if (!disabled) handleSelectMode(mode); }}
                                            disabled={disabled}
                                            title={disabled ? 'Sin fechas activas para editar' : undefined}
                                            className={`px-2 py-1 text-[10px] rounded-md font-bold transition disabled:opacity-40 disabled:cursor-not-allowed ${gridMode === mode ? activeCls : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                        >
                                            {label}
                                        </button>
                                    );
                                    return (
                                        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50/60 flex items-center gap-1.5 flex-wrap shrink-0">
                                            <span className="text-[9px] font-bold text-gray-400 uppercase mr-0.5">{existingCourseEvents.length} prog.</span>
                                            {actionBtn('edit', 'Editar existente', 'bg-blue-100 text-blue-700', editableExistingEvents.length === 0)}
                                            {actionBtn('add', `+ Grupo G${maxG}`, 'bg-green-100 text-green-700')}
                                            {actionBtn('new', `+ Nuevo grupo G${maxG + 1}`, 'bg-purple-100 text-purple-700')}
                                        </div>
                                    );
                                })()}

                                {gridMode === 'edit' && (() => {
                                    const grupos = [...new Set(gridRows.map(r => r.groupNumber || 1))].sort((a, b) => a - b);
                                    return (
                                        <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100 flex items-center gap-1.5 text-[10px] shrink-0">
                                            <FiEdit2 size={11} className="text-blue-600 shrink-0" />
                                            <span className="text-blue-700 font-medium">
                                                Editando grupos activos{grupos.length > 0 ? ` · ${grupos.map(g => `G${g}`).join(', ')}` : ''}
                                            </span>
                                        </div>
                                    );
                                })()}

                                {/* Table header + rows - only show when mode is selected or no existing events */}
                                {(gridMode || existingCourseEvents.length === 0) && (<>
                                <div className="grid grid-cols-[44px_1fr_76px_76px_28px] gap-1.5 px-3 py-2 bg-gray-100 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase shrink-0">
                                    <span>Tipo</span>
                                    <span>Fecha</span>
                                    <span>Inicio</span>
                                    <span>Fin</span>
                                    <button onClick={addGridRow}
                                        title="Agregar fila"
                                        className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition shrink-0">
                                        <FiPlus size={11} />
                                    </button>
                                </div>

                                {/* Table rows */}
                                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                                    {(() => {
                                        // Build enumeration for grid rows
                                        const sortedWithEnum = [...gridRows].filter(r => r.date).sort((a, b) => {
                                            const aStr = a.date + 'T' + (a.startTime || '00:00');
                                            const bStr = b.date + 'T' + (b.startTime || '00:00');
                                            return aStr.localeCompare(bStr);
                                        });
                                        const selCourse = courses.find(c => c.id === gridCourseId);
                                        const mcPerGrp = selCourse?.totalMasterclasses || selCourse?.masterclassesPerGroup || 0;
                                        const clPerGrp = selCourse?.totalSessions || selCourse?.classesPerGroup || 0;
                                        // Numeración por grupo (cada grupo numera desde 1)
                                        const enumMap = {};
                                        const byGroupForEnum = {};
                                        sortedWithEnum.forEach(r => {
                                            const g = r.groupNumber || 1;
                                            (byGroupForEnum[g] = byGroupForEnum[g] || []).push(r);
                                        });
                                        Object.values(byGroupForEnum).forEach(rowsG => {
                                            let mcN = 0, clN = 0;
                                            rowsG
                                                .sort((a, b) => (a.date + 'T' + (a.startTime || '00:00')).localeCompare(b.date + 'T' + (b.startTime || '00:00')))
                                                .forEach(r => {
                                                    if (r.type === 'masterclass') { mcN++; enumMap[r.id] = { n: mcPerGrp > 0 ? ((mcN - 1) % mcPerGrp) + 1 : mcN, t: 'MC' }; }
                                                    else { clN++; enumMap[r.id] = { n: clPerGrp > 0 ? ((clN - 1) % clPerGrp) + 1 : clN, t: 'CL' }; }
                                                });
                                        });

                                        const sortedForRender = [...gridRows].sort((a, b) => {
                                            if (gridMode === 'edit') {
                                                const ga = a.groupNumber || 1, gb = b.groupNumber || 1;
                                                if (ga !== gb) return ga - gb;
                                            }
                                            const aStr = (a.date || '9999-99-99') + 'T' + (a.startTime || '23:59');
                                            const bStr = (b.date || '9999-99-99') + 'T' + (b.startTime || '23:59');
                                            return aStr.localeCompare(bStr);
                                        });

                                        return sortedForRender.map((row, idx) => {
                                            const rowEnum = enumMap[row.id];
                                            // Find conflicts for this specific row
                                            const rowConflict = row.date && row.startTime ? gridConflictSummary.find(c => {
                                                const rowDateStr = new Date(row.date + 'T12:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short' });
                                                return c.dateLabel === rowDateStr && c.time === row.startTime;
                                            }) : null;

                                            const isExisting = !!row._existingEventId;
                                            const showGroupHeader = gridMode === 'edit' && (idx === 0 || (sortedForRender[idx - 1].groupNumber || 1) !== (row.groupNumber || 1));
                                            return (
                                                <div key={row.id}>
                                                    {showGroupHeader && (
                                                        <div className="px-3 py-1 bg-indigo-50 border-b border-indigo-100 text-[9px] font-bold text-indigo-700 uppercase">
                                                            Grupo {row.groupNumber || 1}
                                                        </div>
                                                    )}
                                                    <div className={`grid grid-cols-[44px_1fr_76px_76px_28px] gap-1.5 px-3 py-2 border-b border-gray-100 items-start ${isExisting ? 'border-l-2 border-l-blue-400' : ''} ${row.alert?.startsWith('⛔') ? 'bg-red-50' : row.alert ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                                        <button onClick={() => updateGridRow(row.id, 'type', row.type === 'masterclass' ? 'clase' : 'masterclass')}
                                                            className={`text-[10px] font-bold px-1.5 py-1 rounded min-w-[36px] text-center ${row.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}
                                                            title="Click para cambiar tipo">
                                                            {rowEnum ? `${rowEnum.n} ${rowEnum.t}` : (row.type === 'masterclass' ? 'MC' : 'CL')}
                                                        </button>
                                                        <div>
                                                            <input type="date" value={row.date} onChange={e => updateGridRow(row.id, 'date', e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white focus:ring-1 focus:ring-indigo-400 outline-none" />
                                                            {row.alert && (
                                                                <div className={`mt-1 text-[9px] flex items-center gap-1 leading-tight ${row.alert.startsWith('✅') ? 'text-green-600' : row.alert.startsWith('⛔') ? 'text-red-600' : 'text-amber-600'}`}>
                                                                    {row.alert.startsWith('✅') ? <FiCheck size={9} className="shrink-0" /> : <FiAlertTriangle size={9} className="shrink-0" />}
                                                                    <span className="flex-1">{row.alert}</span>
                                                                    {(row.alert.startsWith('⛔') && !row.override) && (
                                                                        <button onClick={(e) => { e.stopPropagation(); toggleRowOverride(row.id); }}
                                                                            className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[8px] font-bold hover:bg-orange-200 transition shrink-0">
                                                                            Omitir
                                                                        </button>
                                                                    )}
                                                                    {row.override && row.alert.startsWith('✅') && (
                                                                        <button onClick={(e) => { e.stopPropagation(); toggleRowOverride(row.id); }}
                                                                            className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[8px] font-medium hover:bg-gray-200 transition shrink-0">
                                                                            Deshacer
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Hora inicio — select 12h */}
                                                        {(() => {
                                                            const slots = [];
                                                            for (let h = 5; h <= 23; h++) {
                                                                [0, 30].forEach(m => {
                                                                    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                                                    const ampm = h >= 12 ? 'pm' : 'am';
                                                                    const h12 = h % 12 || 12;
                                                                    const label = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                                                                    slots.push({ val, label });
                                                                });
                                                            }
                                                            return (
                                                                <select value={row.startTime} onChange={e => updateGridRow(row.id, 'startTime', e.target.value)}
                                                                    className="px-1.5 py-1.5 border border-gray-200 rounded text-xs bg-white focus:ring-1 focus:ring-indigo-400 outline-none appearance-none cursor-pointer">
                                                                    {slots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                                                </select>
                                                            );
                                                        })()}
                                                        {/* Hora fin — select 12h */}
                                                        {(() => {
                                                            const slots = [];
                                                            for (let h = 5; h <= 23; h++) {
                                                                [0, 30].forEach(m => {
                                                                    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                                                    const ampm = h >= 12 ? 'pm' : 'am';
                                                                    const h12 = h % 12 || 12;
                                                                    const label = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                                                                    slots.push({ val, label });
                                                                });
                                                            }
                                                            return (
                                                                <select value={row.endTime} onChange={e => updateGridRow(row.id, 'endTime', e.target.value)}
                                                                    className="px-1.5 py-1.5 border border-gray-200 rounded text-xs bg-white focus:ring-1 focus:ring-indigo-400 outline-none appearance-none cursor-pointer">
                                                                    {slots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                                                </select>
                                                            );
                                                        })()}
                                                        {gridRows.length > 1 ? (
                                                            <button onClick={() => removeGridRow(row.id)} className="text-gray-300 hover:text-red-500 p-1 mt-0.5"><FiTrash2 size={12} /></button>
                                                        ) : <span />}
                                                    </div>
                                                    {/* Inline conflict warning for this row */}
                                                    {rowConflict && (
                                                        <div className="mx-3 mb-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200">
                                                            <FiAlertTriangle size={10} className="shrink-0" />
                                                            🔴 {rowConflict.count} MC programadas a las {rowConflict.timeLabel || rowConflict.time} pero solo {rowConflict.available || asesores.length} asesores disponibles
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                </>)}

                                {/* Footer buttons */}
                                <div className="px-2 py-1.5 border-t border-gray-200 bg-white shrink-0 space-y-1">
                                    {/* Auto-save status */}
                                    <div className="flex items-center justify-between px-1">
                                        <span className="text-[8px] text-gray-400">
                                            {autoSaveStatus === 'saving' && '⏳ Guardando...'}
                                            {autoSaveStatus === 'saved' && '✅ Auto-guardado'}
                                            {!autoSaveStatus && hasDraft && '☁️ Borrador activo'}
                                        </span>
                                        <button onClick={() => { setShowHistory(!showHistory); }}
                                            className="text-[8px] text-blue-500 hover:text-blue-700 font-medium flex items-center gap-0.5">
                                            <FiClock size={8} /> Historial
                                        </button>
                                    </div>
                                    {/* History dropdown */}
                                    {showHistory && draftHistory.length > 0 && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 max-h-32 overflow-y-auto">
                                            <p className="text-[8px] font-bold text-blue-700 mb-1">Versiones anteriores</p>
                                            {draftHistory.map((v, i) => (
                                                <button key={i} onClick={() => restoreHistoryVersion(v)}
                                                    className="w-full text-left px-2 py-1 text-[9px] hover:bg-blue-100 rounded flex justify-between items-center">
                                                    <span className="text-gray-700">{v.rowCount || 0} filas — {v.editedBy?.displayName || '?'}</span>
                                                    <span className="text-[7px] text-gray-400">{v.editedAt?.seconds ? new Date(v.editedAt.seconds * 1000).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {showHistory && draftHistory.length === 0 && (
                                        <p className="text-[8px] text-gray-400 text-center py-1">Sin historial aún</p>
                                    )}
                                    {/* Action buttons */}
                                    <div className="flex gap-1">
                                        <button onClick={handleInsertGrid} disabled={isInsertingGrid || !gridInstructorId}
                                            className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-indigo-600 text-white text-[9px] font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors shadow-sm">
                                            {isInsertingGrid ? (
                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <><FiCheck size={11} /> {gridMode === 'edit'
                                                    ? (() => {
                                                        const existing = gridRows.filter(r => r.date && r._existingEventId).length;
                                                        const newOnes = gridRows.filter(r => r.date && !r._existingEventId).length;
                                                        return `Guardar${existing ? ` ${existing} mod.` : ''}${newOnes ? ` + ${newOnes} nueva${newOnes > 1 ? 's' : ''}` : ''}`;
                                                    })()
                                                    : `Insertar ${gridRows.filter(r => r.date).length}`
                                                }</>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowInstructorTable(true);
                                                if (gridInstructorId) {
                                                    setFilterInstructorId(gridInstructorId);
                                                } else if (!filterInstructorId && instructors.length > 0) {
                                                    setFilterInstructorId(instructors[0].id);
                                                }
                                                setAgendaHighlightCourseId(null);
                                            }}
                                            className="flex items-center justify-center gap-1 px-2.5 py-1.5 text-[9px] font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200 transition-colors"
                                        >
                                            <FiCalendar size={10} /> Agenda
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Cancel Event Modal - Assign collaborator */}
            {cancelModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setCancelModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 text-white">
                            <h2 className="text-lg font-bold">Cancelar Clase</h2>
                            <p className="text-sm opacity-90">{cancelModal.event?.title} — {new Date(cancelModal.event?.start).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">¿A quién asignar la reprogramación?</label>
                                <HxSelect
                                    className="full"
                                    value={cancelAssignee}
                                    onChange={setCancelAssignee}
                                    options={userOpts(allUsers)}
                                    placeholder="Seleccionar colaborador…"
                                    searchPlaceholder="Buscar colaborador…"
                                    icon={LUser}
                                    ariaLabel="Colaborador que reprograma"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha límite para reprogramar</label>
                                <input type="date" value={cancelDueDate} onChange={e => setCancelDueDate(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-red-400 outline-none" />
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                                <p className="font-bold mb-1">Al confirmar:</p>
                                <ul className="space-y-0.5 list-disc pl-4">
                                    <li>Se cancelará la clase en el calendario</li>
                                    <li>Se creará una tarea automática asignada al colaborador</li>
                                    <li>Se notificará a todos los colaboradores</li>
                                    <li>Al reprogramar la clase, la tarea se completará automáticamente</li>
                                </ul>
                            </div>
                        </div>
                        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
                            <button onClick={() => setCancelModal(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                                Volver
                            </button>
                            <button onClick={executeCancelWithTask} disabled={!cancelAssignee}
                                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-40 transition">
                                Cancelar y Asignar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Create Event Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <FiPlus /> Crear Evento Manual
                            </h2>
                            <p className="opacity-90 text-sm">Programa una clase o masterclass manualmente.</p>
                        </div>

                        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                            {/* Tipo de Evento */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Evento</label>
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setNewEvent({ ...newEvent, type: 'masterclass' })}
                                        className={`flex-1 py-3 rounded-xl font-semibold transition border-2 ${newEvent.type === 'masterclass'
                                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        Masterclass
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewEvent({ ...newEvent, type: 'clase' })}
                                        className={`flex-1 py-3 rounded-xl font-semibold transition border-2 ${newEvent.type === 'clase'
                                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        Clase
                                    </button>
                                </div>
                            </div>

                            {/* Título */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Título</label>
                                <input
                                    type="text"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                                    placeholder="Ej: Masterclass de Introducción"
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                />
                            </div>

                            {/* Instructor */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Instructor</label>
                                <HxSelect
                                    className="full"
                                    value={newEvent.instructorId}
                                    onChange={v => setNewEvent({ ...newEvent, instructorId: v })}
                                    options={instructorOpts()}
                                    placeholder="Seleccionar instructor…"
                                    searchPlaceholder="Buscar instructor…"
                                    icon={LUsers}
                                    ariaLabel="Instructor"
                                />
                            </div>

                            {/* Curso (opcional para masterclass) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Curso {newEvent.type === 'masterclass' && <span className="text-gray-400 font-normal">(opcional)</span>}
                                </label>
                                <HxSelect
                                    className="full"
                                    value={newEvent.courseId}
                                    onChange={v => setNewEvent({ ...newEvent, courseId: v })}
                                    options={courseOpts(courses, 'Sin curso específico')}
                                    placeholder="Sin curso específico"
                                    searchPlaceholder="Buscar curso…"
                                    icon={LBook}
                                    ariaLabel="Curso"
                                />
                            </div>

                            {/* Fecha y Hora Inicio */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Inicio</label>
                                    <input
                                        type="datetime-local"
                                        value={newEvent.start}
                                        onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Fin</label>
                                    <input
                                        type="datetime-local"
                                        value={newEvent.end}
                                        onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none bg-gray-50"
                                    />
                                </div>
                            </div>

                            {/* Availability Alert */}
                            {(() => {
                                if (!newEvent.instructorId || !newEvent.start) return null;
                                const inst = instructors.find(i => i.id === newEvent.instructorId);
                                if (!inst) return null;
                                const dateStr = newEvent.start.split('T')[0];
                                const dayOfWeek = new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                const dayNames = { monday: 'lunes', tuesday: 'martes', wednesday: 'miércoles', thursday: 'jueves', friday: 'viernes', saturday: 'sábados', sunday: 'domingos' };
                                const avail = inst.availability?.[dayOfWeek];
                                const blocked = inst.diasBloqueados?.find(b => b.fecha === dateStr);
                                const alerts = [];
                                if (!avail || avail.length === 0) alerts.push(`${inst.name} no trabaja los ${dayNames[dayOfWeek] || dayOfWeek}`);
                                if (blocked) alerts.push(`Día bloqueado: ${blocked.motivo || 'No disponible'}`);
                                const dayEvents = events.filter(e => {
                                    const eDate = toLocalDateStr(e.start);
                                    return eDate === dateStr && e.resource?.instructorId === newEvent.instructorId;
                                });
                                if (dayEvents.length >= (inst.maxEventosDia || 3)) alerts.push(`Ya tiene ${dayEvents.length} eventos ese día (máx ${inst.maxEventosDia || 3})`);
                                if (alerts.length === 0) return null;
                                return (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                                        {alerts.map((a, i) => (
                                            <div key={i} className="flex items-center gap-2 text-amber-700 text-xs">
                                                <FiAlertTriangle size={13} className="shrink-0 text-amber-500" />
                                                <span>{a}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* Existing schedule for selected course */}
                            {newEvent.courseId && (() => {
                                const courseEvents = events.filter(e => e.resource?.courseId === newEvent.courseId);
                                if (courseEvents.length === 0) return null;
                                return (
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                                        <p className="text-xs font-bold text-blue-700 mb-2">Programación actual del curso ({courseEvents.length} eventos)</p>
                                        <div className="max-h-32 overflow-y-auto space-y-1">
                                            {courseEvents.sort((a, b) => new Date(a.start) - new Date(b.start)).map((e, i) => (
                                                <div key={i} className="flex items-center justify-between text-[11px] text-blue-600 bg-blue-100/50 rounded px-2 py-1">
                                                    <span className="font-medium">{e.title}</span>
                                                    <span className="text-blue-400 font-mono">{new Date(e.start).toLocaleDateString('es', { day: '2-digit', month: 'short' })} {new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Notas */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Notas (opcional)</label>
                                <textarea
                                    value={newEvent.notes}
                                    onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                                    placeholder="Notas adicionales..."
                                    rows={2}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none bg-gray-50 resize-none"
                                />
                            </div>
                        </div>

                        <div className="p-6 pt-0 flex gap-3">
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition"
                                disabled={isCreating}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCreateEvent}
                                disabled={isCreating}
                                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2 shadow-lg shadow-green-200 disabled:opacity-70"
                            >
                                {isCreating ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Creando...
                                    </>
                                ) : (
                                    <>
                                        <FiCheck strokeWidth={3} /> Crear Evento
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Pattern Analysis Modal */}
            {isAnalysisOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-5 text-white flex items-start justify-between">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <FiZap size={22} /> Análisis de Patrones IA
                                </h2>
                                <div className="flex items-center gap-3 mt-1">
                                    <p className="opacity-90 text-xs">
                                        La IA estudia tu calendario y recuerda el historial
                                    </p>
                                    {savedMemory && !isAnalyzing && (
                                        <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <FiRefreshCw size={10} /> Memoria: {savedMemory.fechaAnalisis}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => setIsAnalysisOpen(false)} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition">
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Loading */}
                            {isAnalyzing && (
                                <div className="flex flex-col items-center justify-center py-16 gap-4">
                                    <div className="w-14 h-14 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-gray-600 text-sm font-medium">
                                        {savedMemory ? `Comparando con análisis del ${savedMemory.fechaAnalisis}...` : `Analizando ${events.length + (gcalConnected ? googleEvents.length : 0)} eventos...`}
                                    </p>
                                    <p className="text-gray-400 text-xs text-center max-w-xs">
                                        {savedMemory ? 'La IA está comparando el estado actual con el análisis anterior para detectar avances' : 'Primera vez — la IA está aprendiendo cómo programas tus cursos'}
                                    </p>
                                </div>
                            )}

                            {/* Results */}
                            {!isAnalyzing && analysisResult && (
                                <div className="space-y-4">
                                    {/* Resumen */}
                                    <div className="p-4 bg-orange-50 border border-orange-100 rounded-xl text-sm text-gray-700 leading-relaxed">
                                        <p className="font-semibold text-orange-800 mb-1 flex items-center gap-1">
                                            <FiZap size={13} /> Lo que la IA aprendió:
                                        </p>
                                        {analysisResult.resumen}
                                    </div>

                                    {/* Cambios desde el último análisis */}
                                    {analysisResult.cambios && analysisResult.cambios.length > 0 && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                            <p className="font-semibold text-green-800 text-sm mb-2 flex items-center gap-1">
                                                <FiTrendingUp size={14} /> Cambios desde el último análisis ({savedMemory?.fechaAnalisis}):
                                            </p>
                                            <div className="space-y-1.5">
                                                {analysisResult.cambios.map((c, i) => (
                                                    <div key={i} className="flex items-start gap-2 text-sm">
                                                        <span className="text-green-500 font-bold mt-0.5">↑</span>
                                                        <div>
                                                            <span className="font-semibold text-green-800">{c.curso}: </span>
                                                            <span className="text-green-700">{c.descripcion}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Estadísticas rápidas */}
                                    {analysisResult.estadisticas && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="text-center p-3 bg-blue-50 rounded-xl">
                                                <p className="text-2xl font-bold text-blue-700">{analysisResult.estadisticas.totalEventos}</p>
                                                <p className="text-xs text-blue-600">Eventos</p>
                                            </div>
                                            <div className="text-center p-3 bg-purple-50 rounded-xl">
                                                <p className="text-2xl font-bold text-purple-700">{analysisResult.estadisticas.cursosDetectados || analysisResult.estadisticas.cursosActivos || (analysisResult.cursos || []).length}</p>
                                                <p className="text-xs text-purple-600">Cursos</p>
                                            </div>
                                            <div className="text-center p-3 bg-green-50 rounded-xl">
                                                <p className="text-sm font-bold text-green-700">{(analysisResult.estadisticas.diasConMasCarga || []).join(', ') || '-'}</p>
                                                <p className="text-xs text-green-600">Días con más carga</p>
                                            </div>
                                            <div className="text-center p-3 bg-orange-50 rounded-xl">
                                                <p className="text-sm font-bold text-orange-700">{(analysisResult.estadisticas.horariosPopulares || []).join(', ') || '-'}</p>
                                                <p className="text-xs text-orange-600">Horarios populares</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Sección: Cursos */}
                                    {analysisResult.cursos && analysisResult.cursos.length > 0 && (
                                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                                            <button
                                                onClick={() => setExpandedSection(expandedSection === 'cursos' ? null : 'cursos')}
                                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition text-left"
                                            >
                                                <span className="font-semibold text-gray-700 flex items-center gap-2">
                                                    <FiBookOpen size={15} className="text-purple-500" />
                                                    Cursos detectados
                                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{analysisResult.cursos.length}</span>
                                                </span>
                                                {expandedSection === 'cursos' ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                            </button>
                                            {expandedSection === 'cursos' && (
                                                <div className="p-4 space-y-3">
                                                    {analysisResult.cursos.map((curso, i) => {
                                                        const estadoColor = curso.estado === 'activo'
                                                            ? 'bg-green-100 text-green-700'
                                                            : curso.estado === 'pausado'
                                                                ? 'bg-yellow-100 text-yellow-700'
                                                                : 'bg-gray-100 text-gray-600';
                                                        return (
                                                            <div key={i} className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm text-sm">
                                                                {/* Nombre + estado */}
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <p className="font-bold text-gray-800 text-base">{curso.nombre}</p>
                                                                    {curso.estado && (
                                                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${estadoColor}`}>
                                                                            {curso.estado}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {/* Contadores Masterclass / Sesión */}
                                                                <div className="grid grid-cols-3 gap-2 mb-2">
                                                                    {curso.masterclassActual > 0 && (
                                                                        <div className="text-center p-2 bg-purple-50 rounded-lg">
                                                                            <p className="text-lg font-bold text-purple-700">{curso.masterclassActual}M</p>
                                                                            <p className="text-xs text-purple-500">Masterclass actual</p>
                                                                        </div>
                                                                    )}
                                                                    {curso.sesionActual > 0 && (
                                                                        <div className="text-center p-2 bg-blue-50 rounded-lg">
                                                                            <p className="text-lg font-bold text-blue-700">{curso.sesionActual}</p>
                                                                            <p className="text-xs text-blue-500">Sesión actual</p>
                                                                        </div>
                                                                    )}
                                                                    {curso.totalEventos > 0 && (
                                                                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                            <p className="text-lg font-bold text-gray-700">{curso.totalEventos}</p>
                                                                            <p className="text-xs text-gray-400">Total eventos</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {/* Horario y frecuencia */}
                                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                                                    {curso.diasHabituales && (
                                                                        <span className="flex items-center gap-1">
                                                                            <FiCalendar size={11} /> {curso.diasHabituales}
                                                                        </span>
                                                                    )}
                                                                    {curso.horaHabitual && (
                                                                        <span className="flex items-center gap-1">
                                                                            <FiClock size={11} /> {curso.horaHabitual}
                                                                        </span>
                                                                    )}
                                                                    {curso.frecuenciaDias > 0 && (
                                                                        <span className="flex items-center gap-1">
                                                                            <FiTrendingUp size={11} /> cada ~{curso.frecuenciaDias} días
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {/* Rango de fechas */}
                                                                {(curso.fechaInicio || curso.fechaUltimoEvento) && (
                                                                    <p className="text-xs text-gray-400 mt-1">
                                                                        {curso.fechaInicio && `Inicio: ${curso.fechaInicio}`}
                                                                        {curso.fechaInicio && curso.fechaUltimoEvento && ' · '}
                                                                        {curso.fechaUltimoEvento && `Último: ${curso.fechaUltimoEvento}`}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Sección: Patrones */}
                                    {analysisResult.patrones && analysisResult.patrones.length > 0 && (
                                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                                            <button
                                                onClick={() => setExpandedSection(expandedSection === 'patrones' ? null : 'patrones')}
                                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition text-left"
                                            >
                                                <span className="font-semibold text-gray-700 flex items-center gap-2">
                                                    <FiTrendingUp size={15} className="text-blue-500" />
                                                    Patrones de programación encontrados
                                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{analysisResult.patrones.length}</span>
                                                </span>
                                                {expandedSection === 'patrones' ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                            </button>
                                            {expandedSection === 'patrones' && (
                                                <div className="p-4 space-y-2">
                                                    {analysisResult.patrones.map((patron, i) => {
                                                        const impactColor = patron.impacto === 'alto' ? 'text-red-600 bg-red-50' : patron.impacto === 'medio' ? 'text-yellow-600 bg-yellow-50' : 'text-green-600 bg-green-50';
                                                        return (
                                                            <div key={i} className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-lg text-sm">
                                                                <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${impactColor}`}>
                                                                    {patron.impacto}
                                                                </span>
                                                                <div>
                                                                    <span className="text-xs text-gray-400 uppercase font-medium">{patron.tipo} · </span>
                                                                    <span className="text-gray-700">{patron.descripcion}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Sección: Recomendaciones */}
                                    {analysisResult.recomendaciones && analysisResult.recomendaciones.length > 0 && (
                                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                                            <button
                                                onClick={() => setExpandedSection(expandedSection === 'recomendaciones' ? null : 'recomendaciones')}
                                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition text-left"
                                            >
                                                <span className="font-semibold text-gray-700 flex items-center gap-2">
                                                    <FiAlertTriangle size={15} className="text-orange-500" />
                                                    Recomendaciones para mejorar
                                                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{analysisResult.recomendaciones.length}</span>
                                                </span>
                                                {expandedSection === 'recomendaciones' ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                            </button>
                                            {expandedSection === 'recomendaciones' && (
                                                <div className="p-4 space-y-2">
                                                    {analysisResult.recomendaciones.map((rec, i) => {
                                                        const prioColor = rec.prioridad === 'alta' ? 'border-red-200 bg-red-50' : rec.prioridad === 'media' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50';
                                                        const badgeColor = rec.prioridad === 'alta' ? 'bg-red-100 text-red-700' : rec.prioridad === 'media' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
                                                        return (
                                                            <div key={i} className={`p-3 rounded-lg border text-sm ${prioColor}`}>
                                                                <div className="flex items-start gap-2">
                                                                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${badgeColor}`}>
                                                                        {rec.prioridad}
                                                                    </span>
                                                                    <div>
                                                                        <p className="font-semibold text-gray-800">{rec.titulo}</p>
                                                                        <p className="text-xs text-gray-600 mt-0.5">{rec.detalle}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 flex justify-between items-center">
                            <div className="text-xs text-gray-400 flex flex-col gap-0.5">
                                {analysisResult && <span>{events.length + (gcalConnected ? googleEvents.length : 0)} eventos totales en calendario</span>}
                                {savedMemory && <span className="text-green-600 font-medium flex items-center gap-1"><FiCheck size={11} /> Memoria guardada — próximo análisis comparará avances</span>}
                            </div>
                            <div className="flex gap-2">
                                {analysisResult && (
                                    <button
                                        onClick={handleAnalyzePatterns}
                                        disabled={isAnalyzing}
                                        className="px-4 py-2 text-sm border border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50 transition flex items-center gap-1 disabled:opacity-50"
                                    >
                                        <FiRefreshCw size={14} /> Re-analizar
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsAnalysisOpen(false)}
                                    className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alerts Modal */}
            {showAlerts && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAlerts(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 text-white flex items-center justify-between shrink-0">
                            <div>
                                <h2 className="text-lg font-bold flex items-center gap-2"><FiAlertTriangle size={20} /> Alertas de Capacidad</h2>
                                <p className="text-xs opacity-80 mt-0.5">Meta diaria: {DAILY_MC_GOAL} MC — {asesores.length} asesores activos</p>
                            </div>
                            <button onClick={() => setShowAlerts(false)} className="text-white/80 hover:text-white p-1"><FiX size={20} /></button>
                        </div>

                        {/* Summary cards */}
                        <div className="grid grid-cols-3 gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
                            <div className="bg-red-50 rounded-xl p-3 text-center">
                                <div className="text-2xl font-bold text-red-600">{calendarAlerts.length}</div>
                                <div className="text-[10px] text-red-500 font-medium">Días con conflictos</div>
                            </div>
                            <div className="bg-amber-50 rounded-xl p-3 text-center">
                                <div className="text-2xl font-bold text-amber-600">{calendarAlertsDays.filter(d => d.remaining > 0).length}</div>
                                <div className="text-[10px] text-amber-500 font-medium">Días bajo meta ({DAILY_MC_GOAL} MC)</div>
                            </div>
                            <div className="bg-emerald-50 rounded-xl p-3 text-center">
                                <div className="text-2xl font-bold text-emerald-600">{calendarAlertsDays.reduce((s, d) => s + d.freeSlots.length, 0)}</div>
                                <div className="text-[10px] text-emerald-500 font-medium">Horarios libres disponibles</div>
                            </div>
                        </div>

                        {/* Days list */}
                        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-3">
                            {calendarAlertsDays.length === 0 ? (
                                <div className="text-center py-12 text-gray-400 text-sm">Sin alertas. Todos los días cumplen la meta y no hay conflictos.</div>
                            ) : calendarAlertsDays.map((day, i) => (
                                <div key={i} className={`rounded-xl border p-4 ${day.hasConflict ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                                    {/* Day header */}
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-bold text-gray-800 capitalize">{day.dateLabel}</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${day.totalMC >= DAILY_MC_GOAL ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {day.totalMC}/{DAILY_MC_GOAL} MC
                                            </span>
                                            {day.remaining > 0 && (
                                                <span className="text-[10px] text-amber-600 font-medium">faltan {day.remaining}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Slot details */}
                                    <div className="space-y-1.5 mb-3">
                                        {day.slotDetails.map((slot, j) => (
                                            <div key={j} className="flex items-center gap-2 text-xs">
                                                <span className="font-mono text-gray-500 w-16">{slot.timeLabel}</span>
                                                <div className="flex-1 flex items-center gap-1.5">
                                                    <span className={`font-bold ${slot.excess > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                        {slot.count} MC
                                                    </span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-gray-600">{slot.available} asesores</span>
                                                    {slot.excess > 0 ? (
                                                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">excede por {slot.excess}</span>
                                                    ) : (
                                                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">{slot.free} libres</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Free slots suggestions */}
                                    {day.freeSlots.length > 0 && (
                                        <div className="bg-white rounded-lg p-2.5 border border-gray-100">
                                            <p className="text-[10px] font-bold text-emerald-700 mb-1.5 flex items-center gap-1">
                                                <FiClock size={10} /> Horarios sugeridos para programar MC
                                            </p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {day.freeSlots.map((fs, k) => (
                                                    <span key={k} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-lg font-medium">
                                                        {fs.timeLabel} ({fs.available} asesores)
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Zoom Distribution Modal */}
            {showZoomDist && (
                <div className="zd-scrim" onClick={closeZoomDist}>
                    <div className="hx-page zd-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Distribuir cuentas Zoom">
                        <div className="zd-head">
                            <div>
                                <h2>Distribuir cuentas Zoom</h2>
                                <p>{zoomAccounts.length} cuentas · máx {MAX_SIMULTANEOUS_PER_ACCOUNT} salas a la vez por cuenta · clic en una cuenta para priorizar cursos</p>
                            </div>
                            <div className="tabs" style={{ marginLeft: 'auto' }}>
                                <button type="button" className={zoomDistTab === 'day' ? 'on' : ''} onClick={() => setZoomDistTab('day')}>Día</button>
                                <button type="button" className={zoomDistTab === 'period' ? 'on' : ''} onClick={() => setZoomDistTab('period')}>Periodo</button>
                            </div>
                            <button type="button" className="ibtn" onClick={closeZoomDist} aria-label="Cerrar"><FiX size={18} /></button>
                        </div>

                        <div className="zd-accounts">
                            {zoomAccounts.map(acc => {
                                const priorityCount = (acc.priorityCourseIds || []).length;
                                return (
                                    <button key={acc.id} type="button" className="zd-acc" title="Configurar cursos priorizados"
                                        onClick={() => setPriorityModal({ account: acc, selectedIds: new Set(acc.priorityCourseIds || []) })}>
                                        <i style={{ backgroundColor: acc.color || '#6b7280' }} />
                                        {acc.name}
                                        <small>{(acc.meetings?.length || 0) + (acc.claseMeetings?.length || 0)} salas</small>
                                        {priorityCount > 0 && <b>★ {priorityCount}</b>}
                                    </button>
                                );
                            })}
                        </div>

                        {zoomDistTab === 'period' ? (
                            <ZoomPeriodPanel
                                events={events} accounts={zoomAccounts} courses={courses} options={distOptions}
                                creatingRooms={creatingRooms}
                                onCreateMissing={(items) => handleCreateMissingRooms(items)}
                                onSaveAssignments={async (assignments) => { const r = await saveZoomAssignments(assignments); fetchData(); return r; }}
                                onOpenDay={(d) => { if (!confirmDiscardDay()) return; setZoomDistTab('day'); setZoomDistDate(d); loadZoomDateView(d); }}
                            />
                        ) : (
                        <>
                        <div className="zd-bar">
                            <label className="muted" htmlFor="zd-day" style={{ fontSize: 13 }}>Fecha</label>
                            <input id="zd-day" type="date" className="zd-date" value={zoomDistDate}
                                onChange={e => { setZoomDistDate(e.target.value); loadZoomDateView(e.target.value); }} />
                            <button type="button" className="btn pri" onClick={handleZoomDistribute}>
                                <FiZap size={15} /> {zoomDistIsExisting ? 'Redistribuir desbloqueados' : 'Distribuir automáticamente'}
                            </button>
                            {zoomDistResult && <span className="muted" style={{ fontSize: 12.5 }}>{zoomDistResult.length} eventos</span>}
                            {zoomDistIsExisting && <span className="pill t-green"><FiCheck size={12} /> Distribución guardada</span>}
                            {zoomDistResult && zoomDistResult.some(a => lockedEvents.has(a.eventId)) && (() => {
                                const lockedCount = zoomDistResult.filter(a => lockedEvents.has(a.eventId)).length;
                                return (
                                    <span className="muted" style={{ fontSize: 12.5, marginLeft: 'auto', display: 'inline-flex', gap: 10 }}>
                                        <span style={{ color: 'var(--brand)' }}><FiLock size={11} /> {lockedCount} conservan enlace</span>
                                        <span style={{ color: 'var(--warn)' }}><FiUnlock size={11} /> {zoomDistResult.length - lockedCount} se reasignan</span>
                                    </span>
                                );
                            })()}
                        </div>

                        <div className="zd-body">
                            <div className="zd-main">
                                {!zoomDistResult ? (
                                    <div className="zd-empty">Elige una fecha y pulsa <b>Distribuir automáticamente</b>. Para ver varias semanas usa la pestaña <b>Periodo</b>.</div>
                                ) : zoomDistResult.length === 0 ? (
                                    <div className="zd-empty">No hay eventos en esta fecha.</div>
                                ) : (() => {
                                    const byStart = {};
                                    zoomDistResult.forEach(a => {
                                        if (!byStart[a.startMin]) byStart[a.startMin] = { startLabel: a.startLabel, items: [] };
                                        byStart[a.startMin].items.push(a);
                                    });
                                    return Object.entries(byStart).sort(([a], [b]) => Number(a) - Number(b)).map(([startMin, group]) => (
                                        <div key={startMin} className="zd-group">
                                            <div className="zd-group-h"><FiClock size={11} /> {group.startLabel} <span>· {group.items.length} evento(s)</span></div>
                                            {group.items.slice().sort((x, y) => x.endMin - y.endMin).map(a => {
                                                const bad = !a.accountId || manualNoZoomEvents.has(a.eventId);
                                                const locked = lockedEvents.has(a.eventId);
                                                return (
                                                    <div key={a.eventId} className={`zd-row${bad ? ' is-bad' : ''}${locked ? ' is-locked' : ''}`}>
                                                        <span className="sw" style={{ backgroundColor: a.accountColor }} />
                                                        <div className="tx">
                                                            <div className="tt">{a.eventTitle}</div>
                                                            <div className="mt">
                                                                <span className={`zd-tag ${a.eventType === 'masterclass' ? 'mc' : 'cl'}`}>{a.eventType === 'masterclass' ? 'MC' : 'CL'}</span>{' '}
                                                                {a.accountName}{a.meetingName ? ` · ${a.meetingName}` : ''}
                                                                {a.reusedMcRoom && <span className="zd-tag reuse" style={{ marginLeft: 6 }} title="Usa la sala de una MC de las 5 pm que ya terminó. El bot puede tomar su grabación como masterclass.">sala de MC</span>}
                                                            </div>
                                                        </div>
                                                        <span className="end">→ {a.endLabel}</span>
                                                        {a.zoomLink && <a href={a.zoomLink} target="_blank" rel="noopener noreferrer">Link</a>}
                                                        <button type="button" className={`zd-toggle${manualNoZoomEvents.has(a.eventId) ? ' on' : ''}`} onClick={() => toggleManualNoZoom(a)}
                                                            title={manualNoZoomEvents.has(a.eventId) ? 'Quitar marca "sin Zoom"' : 'Marcar como pendiente de cuenta Zoom'}>
                                                            {manualNoZoomEvents.has(a.eventId) ? 'Sin Zoom ✓' : 'Sin Zoom'}
                                                        </button>
                                                        <button type="button" className="ibtn" style={locked ? { color: 'var(--brand)' } : undefined}
                                                            onClick={() => setLockedEvents(prev => { const next = new Set(prev); if (next.has(a.eventId)) next.delete(a.eventId); else next.add(a.eventId); return next; })}
                                                            title={locked ? 'Desbloquear (permitir recalcular)' : 'Bloquear (conservar al redistribuir)'}>
                                                            {locked ? <FiLock size={13} /> : <FiUnlock size={13} />}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ));
                                })()}
                            </div>

                            <aside className="zd-side">
                                <div className="zd-section">
                                    <h4>Reglas</h4>
                                    <div className="zd-rules">
                                        <span><b>MC</b>: dura 1 h 50 min reales (se ignora el fin del calendario).</span>
                                        <span><b>Clase</b>: su hora de fin + 10 min entre salas.</span>
                                        <span>Cada cuenta: hasta <b>{MAX_SIMULTANEOUS_PER_ACCOUNT} salas a la vez</b>. Una sala nunca se usa en dos eventos que se cruzan.</span>
                                        <label>
                                            <input type="checkbox" checked={distOptions.reuseMcRooms} disabled={!roomSettings} onChange={e => updateDistOptions({ reuseMcRooms: e.target.checked })} />
                                            Las clases pueden usar la sala de una MC de las
                                            <select value={distOptions.reuseMcHour} disabled={!roomSettings || !distOptions.reuseMcRooms}
                                                onChange={e => updateDistOptions({ reuseMcHour: Number(e.target.value) })}>
                                                {[15, 16, 17, 18, 19].map(h => <option key={h} value={h}>{h - 12}:00 pm</option>)}
                                            </select>
                                            que ya terminó
                                        </label>
                                    </div>
                                </div>

                                {autoCreateOnDistribute && missingRoomItems.length > 0 && (
                                    <div className="zd-section">
                                        <button type="button" className="btn sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleCreateMissingRooms()} disabled={creatingRooms}>
                                            <FiZap size={13} /> {creatingRooms ? 'Creando en Zoom…' : `Crear ${missingRoomItems.length} sala(s) faltante(s) en Zoom`}
                                        </button>
                                    </div>
                                )}
                                {zoomDistWarnings.length > 0 && (
                                    <div className="zd-section zd-warn">
                                        <b><FiAlertTriangle size={12} /> Avisos ({zoomDistWarnings.length})</b>
                                        <ul>{zoomDistWarnings.map((w, i) => <li key={i}>{w.text}</li>)}</ul>
                                    </div>
                                )}

                                <div className="zd-section">
                                    <h4>Por cuenta</h4>
                                    {!zoomDistResult ? (
                                        <p className="muted" style={{ fontSize: 13 }}>Distribuye para ver la carga de cada cuenta.</p>
                                    ) : (() => {
                                        const byAcc = {};
                                        zoomDistResult.forEach(a => {
                                            if (!a.accountId) return;
                                            if (!byAcc[a.accountId]) byAcc[a.accountId] = { account: zoomAccounts.find(z => z.id === a.accountId), items: [] };
                                            byAcc[a.accountId].items.push(a);
                                        });
                                        const groups = Object.values(byAcc);
                                        if (!groups.length) return <p className="muted" style={{ fontSize: 13 }}>Ningún evento tiene cuenta todavía.</p>;
                                        return groups.map(({ account, items }) => {
                                            const sorted = items.slice().sort((a, b) => a.startMin - b.startMin);
                                            const peak = peakSimultaneous(sorted);
                                            const isOver = peak > MAX_SIMULTANEOUS_PER_ACCOUNT;
                                            // Hora a partir de la cual ya hay MAX otros eventos activos: este evento no debe pasarse de ella.
                                            const deadlineOf = (target, others) => {
                                                const pts = [];
                                                others.forEach(o => { pts.push({ time: o.effectiveStartMin, kind: 'start', ev: o }); pts.push({ time: o.effectiveEndMin, kind: 'end', ev: o }); });
                                                pts.sort((a, b) => a.time - b.time || (a.kind === 'end' ? -1 : 1));
                                                const active = new Set();
                                                for (const p of pts) {
                                                    if (p.kind === 'start') active.add(p.ev); else active.delete(p.ev);
                                                    if (active.size >= MAX_SIMULTANEOUS_PER_ACCOUNT && p.kind === 'start' && p.time >= target.effectiveStartMin) return { time: p.time, causes: [...active] };
                                                }
                                                return null;
                                            };
                                            const alerts = sorted.map(ev => {
                                                const dl = deadlineOf(ev, sorted.filter(o => o !== ev));
                                                if (!dl) return null;
                                                return { bad: ev.effectiveEndMin > dl.time, text: `"${ev.eventTitle}" no debe pasar de las ${fmtTimeLabel(dl.time)}: a esa hora ya están ${dl.causes.map(c => `"${c.eventTitle}"`).join(' y ')}. Termina ${fmtTimeLabel(ev.effectiveEndMin)}.` };
                                            }).filter(Boolean);
                                            const why = isOver ? `Pico de ${peak} a la vez: supera el límite de ${MAX_SIMULTANEOUS_PER_ACCOUNT}. Reasigna manualmente.`
                                                : sorted.length === 1 ? 'Un solo evento.'
                                                : peak === 1 ? 'En secuencia: ninguno se cruza.'
                                                : `Hasta ${peak} a la vez, dentro del límite.`;
                                            return (
                                                <div key={account?.id} className="zd-acard">
                                                    <div className="h">
                                                        <i style={{ backgroundColor: account?.color || '#6b7280' }} />
                                                        {account?.name}
                                                        <span className={`pill ${isOver ? 't-red' : 't-green'}`}>{items.length} ev · pico {peak}/{MAX_SIMULTANEOUS_PER_ACCOUNT}</span>
                                                    </div>
                                                    <p className={`why${isOver ? ' bad' : ''}`}>{why}</p>
                                                    <ul>
                                                        {sorted.map(it => {
                                                            const n = concurrentDuring(it, sorted);
                                                            return (
                                                                <li key={it.eventId}>
                                                                    <time>{fmtTimeLabel(it.startMin)}</time>
                                                                    <span>{it.eventTitle}<small>termina {fmtTimeLabel(it.effectiveEndMin)}{n > 1 ? ` · ${n} a la vez` : ''}{it.reusedMcRoom ? ' · sala de MC' : ''}</small></span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                    {alerts.length > 0 && <div className="dl">{alerts.map((n, k) => <span key={k} className={n.bad ? 'bad' : ''}>⚠ {n.text}</span>)}</div>}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </aside>
                        </div>

                        {zoomDistResult && zoomDistResult.length > 0 && (() => {
                            const noAccountCount = zoomDistResult.filter(a => !a.accountId).length;
                            const manualCount = zoomDistResult.filter(a => a.accountId && manualNoZoomEvents.has(a.eventId)).length;
                            const blockedCount = noAccountCount + manualCount;
                            return (
                                <div className="zd-foot">
                                    {blockedCount > 0 && (
                                        <span className="msg">
                                            {blockedCount} evento(s) sin Zoom{noAccountCount > 0 && manualCount > 0 ? ` (${noAccountCount} sin cuenta · ${manualCount} marcados)` : ''}: resuélvelos antes de guardar
                                        </span>
                                    )}
                                    <div className="acts">
                                        <button type="button" className="btn" onClick={closeZoomDist}>Cancelar</button>
                                        <button type="button" className="btn pri" onClick={handleZoomSave} disabled={zoomDistSaving || blockedCount > 0}
                                            title={blockedCount > 0 ? `${blockedCount} evento(s) sin Zoom` : 'Guardar distribución'}>
                                            {zoomDistSaving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiCheck size={14} />} Guardar distribución
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                        </>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm recalc — date already has a saved Zoom distribution */}
            {zoomRecalcConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setZoomRecalcConfirm(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-amber-50 px-6 py-5 flex flex-col items-center text-center">
                            <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-3 ring-4 ring-amber-100">
                                <FiAlertTriangle size={24} />
                            </div>
                            <h3 className="text-base font-bold text-gray-800">Redistribuir desbloqueados</h3>
                            {(() => {
                                const totalCount = zoomDistResult?.length || 0;
                                const lockedCount = zoomDistResult ? zoomDistResult.filter(a => lockedEvents.has(a.eventId)).length : 0;
                                const unlockedCount = totalCount - lockedCount;
                                return (
                                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                        El <strong>{zoomDistDate}</strong> ya tiene una distribución guardada.<br/>
                                        <span className="inline-flex items-center gap-1 mt-2 text-blue-700"><FiLock size={11} /> {lockedCount} evento(s) bloqueado(s): <strong>conservan su enlace actual</strong>.</span><br/>
                                        <span className="inline-flex items-center gap-1 text-amber-700"><FiUnlock size={11} /> {unlockedCount} evento(s) desbloqueado(s): <strong>serán reasignados</strong>.</span><br/>
                                        ¿Continuar?
                                    </p>
                                );
                            })()}
                        </div>
                        <div className="flex gap-3 px-6 py-4 bg-white">
                            <button onClick={() => setZoomRecalcConfirm(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
                                Cancelar
                            </button>
                            <button onClick={runZoomDistribute} className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-medium transition active:scale-[0.98]">
                                Sí, redistribuir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Priority Courses Modal (per zoom account) */}
            {priorityModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => setPriorityModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 shrink-0" style={{ background: `linear-gradient(135deg, ${priorityModal.account.color}22, transparent)` }}>
                            <span className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: priorityModal.account.color }}>
                                <FiStar size={16} className="text-white" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-gray-800 truncate">Priorizar cursos</h3>
                                <p className="text-[11px] text-gray-500 truncate">{priorityModal.account.name}</p>
                            </div>
                            <button onClick={() => setPriorityModal(null)} className="text-gray-400 hover:text-gray-600 p-1"><FiX size={18} /></button>
                        </div>
                        <p className="px-5 pt-3 text-[11px] text-gray-500 leading-relaxed shrink-0">
                            Los eventos de los cursos seleccionados intentarán usar esta cuenta primero. Si está saturada al mismo tiempo, el sistema asigna otra y muestra un aviso.
                        </p>
                        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
                            {courses.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-6">No hay cursos disponibles.</p>
                            ) : (
                                courses
                                    .slice()
                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                    .map(c => {
                                        const checked = priorityModal.selectedIds.has(c.id);
                                        return (
                                            <label key={c.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition border ${checked ? 'bg-blue-50 border-blue-200' : 'border-transparent hover:bg-gray-50'}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        const next = new Set(priorityModal.selectedIds);
                                                        if (checked) next.delete(c.id); else next.add(c.id);
                                                        setPriorityModal({ ...priorityModal, selectedIds: next });
                                                    }}
                                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-xs font-medium text-gray-700 flex-1 truncate">{c.name}</span>
                                                {c.abreviatura && (
                                                    <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{c.abreviatura}</span>
                                                )}
                                            </label>
                                        );
                                    })
                            )}
                        </div>
                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0 bg-gray-50">
                            <span className="text-[11px] text-gray-500">{priorityModal.selectedIds.size} curso(s) seleccionado(s)</span>
                            <div className="flex gap-2">
                                <button onClick={() => setPriorityModal(null)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition">
                                    Cancelar
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            await updateZoomAccount(priorityModal.account.id, {
                                                priorityCourseIds: [...priorityModal.selectedIds],
                                            });
                                            toast.success('Prioridades guardadas');
                                            setPriorityModal(null);
                                            // Redistribuir con las prioridades nuevas cuando llegue la cuenta actualizada,
                                            // respetando los eventos bloqueados (mismo mecanismo que al crear salas).
                                            if (zoomDistResult) {
                                                accountsAtCreateRef.current = zoomAccounts;
                                                setRedistributePending(true);
                                            }
                                        } catch (err) {
                                            console.error(err);
                                            toast.error('Error al guardar prioridades');
                                        }
                                    }}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition flex items-center gap-1.5">
                                    <FiCheck size={12} /> Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Wizard Modal */}
            {isAiModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <FiCpu /> Generador IA
                            </h2>
                            <p className="opacity-90 text-sm">Configura los parámetros para generar o replantear horarios.</p>
                        </div>

                        <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
                            {/* Curso específico o todos */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Curso a Programar</label>
                                <HxSelect
                                    className="full"
                                    value={selectedCourseId}
                                    onChange={setSelectedCourseId}
                                    options={courseOpts(courses, 'Todos los cursos')}
                                    placeholder="Todos los cursos"
                                    searchPlaceholder="Buscar curso…"
                                    icon={LBook}
                                    ariaLabel="Curso a programar"
                                />
                            </div>

                            {/* Mes y Año en fila */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mes</label>
                                    <select
                                        value={targetMonth}
                                        onChange={e => setTargetMonth(Number(e.target.value))}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none bg-gray-50"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('es', { month: 'long' })}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Año</label>
                                    <input
                                        type="number"
                                        value={targetYear}
                                        onChange={e => setTargetYear(Number(e.target.value))}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none bg-gray-50"
                                    />
                                </div>
                            </div>

                            {/* Cantidad de Masterclass */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Masterclass por Curso
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={masterclassCount}
                                    onChange={e => setMasterclassCount(Number(e.target.value))}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none bg-gray-50"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Cantidad mínima de masterclass a programar por curso
                                </p>
                            </div>

                            {/* Regenerate mode toggle */}
                            {selectedCourseId && (
                                <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={regenerateMode}
                                            onChange={e => setRegenerateMode(e.target.checked)}
                                            className="w-5 h-5 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <div>
                                            <span className="font-semibold text-orange-800 flex items-center gap-2">
                                                <FiTrash2 size={16} /> Replantear (eliminar y regenerar)
                                            </span>
                                            <p className="text-xs text-orange-700">
                                                Elimina todos los eventos existentes de este curso en el mes seleccionado antes de generar nuevos.
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            )}

                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <h4 className="font-semibold text-blue-800 text-sm mb-1">La IA considerará:</h4>
                                <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
                                    <li>Empezar masterclass desde el primer día hábil</li>
                                    <li>Programar {masterclassCount} MC mínimo por curso</li>
                                    <li>Respetar disponibilidad de instructores</li>
                                    <li>Distribuir eventos sin saturar días</li>
                                    {gcalConnected && googleEvents.length > 0 && (
                                        <li className="text-green-700 font-medium">
                                            Evitar conflictos con {googleEvents.length} eventos de Google Calendar
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => {
                                        setIsAiModalOpen(false);
                                        setSelectedCourseId('');
                                        setRegenerateMode(false);
                                    }}
                                    className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition"
                                    disabled={isGenerating}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGenerateAI}
                                    disabled={isGenerating}
                                    className={`flex-1 py-3 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2 shadow-lg disabled:opacity-70 ${regenerateMode
                                        ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-200'
                                        : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200'
                                        }`}
                                >
                                    {isGenerating ? (
                                        <>
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            {regenerateMode ? 'Replanteando...' : 'Generando...'}
                                        </>
                                    ) : (
                                        <>
                                            <FiCheck strokeWidth={3} />
                                            {regenerateMode ? 'Replantear' : 'Generar'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showGCalExport && (
                <GoogleCalendarExportPanel instructors={instructors} events={events} courses={courses} onlyInstructorId={instructorMode ? viewerInstructorId : null} onClose={() => setShowGCalExport(false)} />
            )}

            {/* GCal Import Modal */}
            {isGCalImportOpen && (
                <GCalImportModal
                    instructors={instructors}
                    courses={courses}
                    onClose={() => setIsGCalImportOpen(false)}
                    onImported={(count) => {
                        toast.success(`${count} eventos importados desde Google Calendar`);
                        setIsGCalImportOpen(false);
                        fetchData();
                    }}
                />
            )}

            {/* AI Scheduling Chat Panel */}
            <AISchedulingChat
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                events={[...events, ...(gcalConnected ? googleEvents : [])]}
                instructors={instructors}
                courses={courses}
                savedMemory={savedMemory}
                onCreateEvents={handleChatCreateEvents}
            />

            {/* Reprogramar clase cancelada */}
            {reprogramTarget && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setReprogramTarget(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                        <h4 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2"><FiRefreshCw size={14} className="text-amber-600" /> Reprogramar clase</h4>
                        <p className="text-[11px] text-gray-500 mb-4 truncate">{reprogramTarget.title || 'Clase'}</p>
                        <label className="block text-[11px] font-semibold text-gray-600 mb-1">Nueva fecha</label>
                        <input type="date" value={reprogramForm.date}
                            onChange={(e) => setReprogramForm(f => ({ ...f, date: e.target.value }))}
                            className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-600 mb-1">Inicio</label>
                                <input type="time" value={reprogramForm.startTime}
                                    onChange={(e) => setReprogramForm(f => ({ ...f, startTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-600 mb-1">Fin</label>
                                <input type="time" value={reprogramForm.endTime}
                                    onChange={(e) => setReprogramForm(f => ({ ...f, endTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setReprogramTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                            <button onClick={executeReprogram} className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 flex items-center gap-1.5"><FiRefreshCw size={14} /> Reprogramar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Agenda Expand Modal */}
            <AgendaExpandModal
                open={showAgendaExpand}
                onClose={() => setShowAgendaExpand(false)}
                instructor={instructors.find(i => i.id === filterInstructorId) || null}
                courses={courses}
                events={filterInstructorId
                    ? events.filter(e => (e.resource?.instructorId || e.instructorId) === filterInstructorId)
                          .sort((a, b) => new Date(a.start) - new Date(b.start))
                    : []}
                isGroupInactive={showHiddenGroups ? undefined : isGroupInactive}
            />
        </div>
    );
};

export default CalendarPage;
